/* eslint-disable @next/next/no-img-element */
import * as React from 'react'
import * as RadixAccordion from '@radix-ui/react-accordion'
import {
  CaretSortIcon,
  ChevronDownIcon,
  Cross1Icon,
  Cross2Icon,
  DragHandleDots2Icon,
  EnterIcon,
  ExternalLinkIcon,
  InfoCircledIcon,
  MagnifyingGlassIcon,
  Pencil1Icon,
  PlusIcon,
  CheckIcon,
  DotsHorizontalIcon,
  TrashIcon,
} from '@radix-ui/react-icons'
import {
  Collapsible,
  CollapsibleContent as RadixCollapsibleContent,
  CollapsibleTrigger,
} from '~/sotano-kit/primitives/collapsible'
import { clsx } from 'clsx'
import { capitalCase } from 'change-case'
import {
  CodeSnippet,
  CodeSnippetProps,
} from '~/sotano-kit/primitives/code-snippet'
import { Input, Label } from '~/sotano-kit/primitives/input'
import {
  SimpleTooltip,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '~/sotano-kit/primitives/tooltip'
import { useSyncedValue } from '~/hooks/use-synced-value'
import { Checkbox, LabeledCheckbox } from '~/sotano-kit/primitives/checkbox'
import { ScrollArea } from '~/sotano-kit/primitives/scroll-area'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/sotano-kit/primitives/select'
import { ItemIndicator } from '@radix-ui/react-select'
import {
  Block,
  isComponentBlock,
  isDocumentBlock,
  isInstanceBlock,
  isListBlock,
  checkIfApiNameExists,
  checkIfSlugExists,
  getApiNameAndSlug,
  getTitleUpdateOverride,
  getTitleBasedOnSelectedVariants,
  isReferenceBlock,
  isAgentBlock,
} from '@basehub/data/replicache/index'
import { blockMetadata } from '~/replicache/frontend/block-metadata'
import Link, { LinkProps } from 'next/link'
import { BlockIcon } from '~/components/block-icon'
import {
  IconButton,
  IconButtonProps,
} from '~/sotano-kit/primitives/icon-button'
import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import { Button, ButtonLink } from '~/sotano-kit/primitives/button'
import { useTabsStore } from '../tabs/store'
import { invariant } from '@epic-web/invariant'
import { useRepoStore } from '../repo-store'
import { useDashboardSlugs } from '~/hooks/use-dashboard-slugs'
import Selector, {
  SelectorEmptyState,
  SelectorItem,
  SelectorProps,
} from '~/sotano-kit/primitives/selector'
import { ComboboxOption } from '~/hooks/use-combobox-state'
import { PlusIcon as ReferencePlusIcon } from '../blocks/reference/input'
import { match } from 'ts-pattern'
import { useToggleState } from '~/hooks/use-toggle-state'
import { flip } from '~/lib/gsap/flip'
import { toast } from 'sonner'
import { useWorkingTreeStore } from '~/hooks-data/blocks-tree/provider'
import { SidebarOpenedIcon } from '~/components/icons/sidebar-opened'
import {
  getApiNameFromTitle,
  getSlugFromTitle,
} from '@basehub/data/api-kv/index'
import { useUserStore } from '~/app/_store/user-store'
import { useEditorMode } from '../editor-mode-provider'
import {
  VALID_IMAGE_MIME_TYPES,
  VALID_VIDEO_MIME_TYPES,
} from '@basehub/data/replicache/schema/block/valid-mime-types'
import { useUploadFileToRepoMutation } from '~/hooks-data/use-upload-file-to-repo-mutation'
import { cva } from 'class-variance-authority'
import { title } from '~/lib/title'
import {
  useIsPropertiesPanelCollapsed__PUBLIC,
  usePropertiesPanelMode__PUBLIC,
} from './pp-layout'
import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CheckedState } from '@radix-ui/react-checkbox'
import { CSS } from '@dnd-kit/utilities'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'

import {
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenu,
  DropdownMenuContent,
} from '~/sotano-kit/primitives/dropdown-menu'
import { replaceAssetsURL, MaxSizeUnit } from '@basehub/utils/image-utils'
import { useTextAreaAutoGrow } from '~/sotano-kit/primitives/textarea/auto-grow'
import { mergeRefs } from '~/lib/merge-refs'
import { useFlags } from '~/lib/flags/provider'
import {
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTrigger,
} from '~/sotano-kit/primitives/popover'
import { PopoverAnchor } from '@radix-ui/react-popover'
import { getSolidColor } from '../blocks/variant-set/utils'
import { useSelectedVariants } from '~/hooks/use-selected-variants'
import { useRepoUndoManager } from '~/replicache/frontend/undo-redo/manager'
import {
  UpdateApiNameParams,
  UpdateBlockParams,
  UpdateSlugParams,
} from '@basehub/data/replicache/mutators/repo-space/block/mutators/crud'
import { getBlockFocusOp } from '~/replicache/frontend/undo-redo/util'
import { BlockHistory } from './block-history'
import { useUserPreferences } from '~/hooks-data/use-user-preferences'
import { useTreeSlice } from '~/hooks/use-tree-slice'
import { useBranchOrCommitSegment } from '~/lib/commits/use-selected-branch-or-commit'

export const Root = ({
  children,
  block,
  parentBlock,
}: {
  children?: React.ReactNode
  block: Block
  parentBlock: Block | null
}) => {
  const { data } = useRepoReplicache()
  const optimisticUser = useUserStore((s) => s.optimisticUser)
  const setFocus = useRepoStore((s) => s.setFocus)
  const editorMode = useEditorMode()
  const addTab = useTabsStore((s) => s.addTab)
  const { teamSlug, repoSlug, activeTabKey: blockId } = useDashboardSlugs()
  const { encodedSegment: branchOrCommitSegment } = useBranchOrCommitSegment()
  const [, setIsCollapsed__PUBLIC] = useIsPropertiesPanelCollapsed__PUBLIC()
  const [sidebarTabSelected__PUBLIC, setPropertiesPanelMode__PUBLIC] =
    usePropertiesPanelMode__PUBLIC()
  const sidebarTabSelected__PREFERENCES = useUserPreferences(
    (s) => s.propertiesPanelMode
  )

  const sidebarTabSelected = React.useMemo(() => {
    if (optimisticUser?.id && data?.replicache) {
      return sidebarTabSelected__PREFERENCES
    }
    return sidebarTabSelected__PUBLIC ?? 'properties'
  }, [
    data?.replicache,
    optimisticUser?.id,
    sidebarTabSelected__PREFERENCES,
    sidebarTabSelected__PUBLIC,
  ])

  const getNode = useWorkingTreeStore((s) => s.treeRef.current?.getNode)

  const setPropertiesPanelMode = React.useCallback(
    (mode: 'history' | 'properties') => {
      if (!optimisticUser?.id || !data?.replicache.mutate) {
        setPropertiesPanelMode__PUBLIC(mode)
        return
      }

      return data.replicache.mutate.setPropertiesPanelMode({
        userId: optimisticUser.id,
        mode,
      })
    },
    [
      data?.replicache.mutate,
      optimisticUser?.id,
      setPropertiesPanelMode__PUBLIC,
    ]
  )

  return (
    <ScrollArea
      className="h-full overflow-auto [&>div[data-radix-scroll-area-viewport]]:px-5 text-sm"
      onContextMenu={(e) => e.stopPropagation()}
      viewportClassName="[&>div]:!h-full"
      data-properties-panel={idToSourceBlockId(block)}
    >
      <div
        className={clsx(
          'bg-dark-bg z-20 sticky top-0 pt-[3px] gap-x-3 gap-y-[5.75px] items-center border-b border-transparent flex justify-between w-full',
          'min-h-[40px]' // to align with the height of the tabs
        )}
      >
        <Button
          unstyled
          focusStyled
          data-active={sidebarTabSelected === 'properties'}
          className="text-xs cursor-default leading-normal text-dark-faint hover:text-dark-dim data-[active=true]:text-dark-bright font-medium"
          onClick={() => setPropertiesPanelMode('properties')}
        >
          Properties
        </Button>
        <Button
          unstyled
          focusStyled
          data-active={sidebarTabSelected === 'history'}
          className="text-xs cursor-default leading-normal text-dark-faint hover:text-dark-dim data-[active=true]:text-dark-bright font-medium"
          onClick={() => setPropertiesPanelMode('history')}
        >
          History
        </Button>
        <Tooltip>
          <TooltipTrigger asChild>
            <IconButton
              className="text-dark-faint hover:text-dark-dim group ml-auto"
              onClick={async () => {
                if (!data?.replicache || !optimisticUser) {
                  setIsCollapsed__PUBLIC(true)
                  return
                }
                await data.replicache.mutate.toggleCollapsePropertiesPanel(
                  optimisticUser.id
                )
              }}
            >
              <SidebarOpenedIcon className="text-dark-muted group-hover:text-inherit size-4" />
            </IconButton>
          </TooltipTrigger>
          <TooltipContent side="left" sideOffset={8}>
            Close properties panel
          </TooltipContent>
        </Tooltip>
      </div>
      <PropertiesContainer>
        <div className="flex items-center justify-between w-full mt-1.5">
          <div className="text-dark-dim flex items-center">
            <div className="text-dark-dim flex items-center space-x-2">
              <BlockIcon block={block} />
              <p className="text-sm font-medium capitalize">
                {blockMetadata.find((b) => b.type === block.type)?.label ??
                  block.type}
              </p>
            </div>
          </div>
          <SimpleTooltip content="Copy block ID" align="center" side="left">
            <Button
              aria-label="Copy block ID"
              intent="secondary"
              size="xs"
              className="leading-5 !font-bold !px-0.5 !rounded min-w-[20px]"
              onClick={async () => {
                navigator.clipboard.writeText(idToSourceBlockId(block))
                toast.success('Block ID copied to clipboard')
              }}
            >
              ID
            </Button>
          </SimpleTooltip>
        </div>
      </PropertiesContainer>
      {editorMode !== 'write:all' &&
        sidebarTabSelected === 'properties' &&
        parentBlock &&
        isInstanceBlock(parentBlock) &&
        (() => {
          invariant(
            block.instanceChildTarget,
            'block.instanceChildTarget not found'
          )

          return (
            <ButtonLink
              intent="secondary"
              size="small"
              href={`/${teamSlug}/${repoSlug}/explore/${branchOrCommitSegment}/${
                parentBlock.typeOptions.targetBlockId
              }#${block.instanceChildTarget}`}
              className="mt-2 inline-block w-full"
              onClick={(e) => {
                if (e.metaKey || e.ctrlKey) return

                const activeDocument = blockId
                  ? getNode?.(blockId, {
                      fallbackKey: 'sourceBlockId',
                    })?.value
                  : null

                setFocus({
                  type: 'block:wrapper',
                  blockId: block.instanceChildTarget!,
                  temporaryBackLink: {
                    title: activeDocument?.title || 'Untitled',
                    id: idToSourceBlockId(activeDocument ?? block),
                    edgeId: activeDocument
                      ? idToSourceBlockId(block)
                      : undefined,
                  },
                })

                addTab({
                  key: parentBlock.typeOptions.targetBlockId,
                  partiallyOpen: true,
                })
              }}
            >
              Configure in component
            </ButtonLink>
          )
        })()}
      {children}
      {sidebarTabSelected === 'history' && <BlockHistory block={block} />}
      <div className="h-16" />
    </ScrollArea>
  )
}

export const Property = ({
  name,
  value,
  autoReplaceAssetsURL = true,
}: {
  name: string
  value: string | number
  autoReplaceAssetsURL?: boolean
}) => {
  const [isCopied, setIsCopied] = React.useState(false)

  const processedValue = autoReplaceAssetsURL ? replaceAssetsURL(value) : value

  React.useEffect(() => {
    if (isCopied) navigator.clipboard.writeText(processedValue.toString())
  }, [processedValue, isCopied])

  return (
    <Tooltip
      delayDuration={0}
      onOpenChange={(isOpen) => {
        if (!isOpen) setIsCopied(false)
      }}
    >
      <p className="text-xs flex overflow-hidden">
        <span className="text-dark-faint">{name}</span>
        <TooltipTrigger asChild>
          <span
            className="ml-2 cursor-default break-all overflow-hidden whitespace-nowrap text-ellipsis"
            onClick={(e) => {
              e.preventDefault()
              setIsCopied(true)
            }}
          >
            {processedValue}
          </span>
        </TooltipTrigger>
      </p>
      <TooltipContent
        sideOffset={10}
        className="inline-block w-16 overflow-hidden !px-2 py-1 text-center"
      >
        {isCopied ? 'Copied!' : 'Copy'}
      </TooltipContent>
    </Tooltip>
  )
}

export const Group = ({
  children,
  title,
  className,
  noBorderTop,
  noBorderBottom,
  tooltipInfo,
  spacing,
  titleClassName,
  style,
}: {
  children: React.ReactNode
  title?: React.ReactNode
  className?: string
  noBorderTop?: boolean
  noBorderBottom?: boolean
  tooltipInfo?: React.ReactNode
  spacing?: 'tight' | 'normal' | 'loose' | 'none'
  titleClassName?: string
  style?: React.CSSProperties
}) => {
  // We hide the group entirely if all children return null
  const [hidden, setHidden] = React.useState(false)
  const childrenContainer = React.useRef<HTMLDivElement>(null)
  const editorMode = useEditorMode()

  React.useEffect(() => {
    setHidden(childrenContainer.current?.children.length === 0)
  }, [editorMode, children])

  return (
    <div
      className={clsx(
        'border-dark-border-solid flex flex-col border-t py-4 last:border-b-0',
        className,
        noBorderTop && '!border-t-0 pt-0',
        noBorderBottom && '!border-b-0',
        hidden && 'hidden'
      )}
      style={style}
    >
      {title && (
        <span
          className={clsx(
            'mb-1.5 inline-flex items-center gap-2',
            titleClassName
          )}
        >
          <div
            className={clsx(
              'text-dark-dim text-sm font-medium leading-normal flex items-center',
              !tooltipInfo && 'w-full'
            )}
          >
            {title}
          </div>
          {tooltipInfo && (
            <Tooltip delayDuration={500}>
              <TooltipTrigger className="cursor-default" asChild>
                <InfoCircledIcon className="ml-1 text-dark-muted hover:text-dark-bright" />
              </TooltipTrigger>
              <TooltipContent>{tooltipInfo}</TooltipContent>
            </Tooltip>
          )}
        </span>
      )}

      <div
        ref={childrenContainer}
        className={clsx(
          'flex flex-col',
          match(spacing)
            .with('none', () => '')
            .with('tight', () => 'space-y-2')
            .with('normal', () => 'space-y-3')
            .with('loose', () => 'space-y-4')
            .otherwise(() => 'space-y-3')
        )}
      >
        {children}
      </div>
    </div>
  )
}

export const CollapsibleGroup = ({
  children,
  title,
  className,
  noBorderTop,
  noBorderBottom,
  id,
  disabled,
}: {
  children: React.ReactNode
  title?: React.ReactNode
  className?: string
  noBorderTop?: boolean
  noBorderBottom?: boolean
  id: string
  disabled?: boolean
}) => {
  const replicache = useRepoReplicache().data?.replicache
  const userId = useUserStore((s) => s.optimisticUser?.id)
  // TODO: Handle collapsible for non-logged-users
  const setIsOpen = React.useCallback(
    (v: boolean) => {
      if (!replicache || !userId) return
      replicache.mutate.setCollapsedState({
        groupId: id,
        userId,
        isCollapsed: v,
      })
    },
    [id, replicache, userId]
  )
  let isOpen = useUserPreferences((s) => s.collapsibleGroups[id] ?? false)
  if (disabled) {
    isOpen = false
  }

  return (
    <Collapsible
      className={clsx(
        'border-dark-border-solid flex flex-col border-t py-4 last:border-b-0',
        className,
        noBorderTop && '!border-t-0 pt-0',
        noBorderBottom && '!border-b-0'
      )}
      open={isOpen}
      onOpenChange={setIsOpen}
      disabled={disabled}
    >
      {children}
      <CollapsibleTrigger asChild>
        <Button
          unstyled
          focusStyled
          className={clsx(
            'group/trigger cursor-default hover:text-dark-bright transition-colors flex items-center gap-1 text-[0.6875rem] text-dark-faint font-medium',
            disabled && 'hidden'
          )}
        >
          <ChevronDownIcon
            className="group-data-[state=open]/trigger:rotate-180 group-data-[state=open]/trigger:translate-y-px"
            height={15}
            width={15}
          />
          <span className="leading-[0.9375rem]">
            {isOpen ? 'Hide' : 'Show'} {title}
          </span>
        </Button>
      </CollapsibleTrigger>
    </Collapsible>
  )
}

export const AlwaysVisibleContent = ({
  children,
  spacing,
  className,
}: {
  children: React.ReactNode
  spacing?: 'tight' | 'normal' | 'loose' | 'none'
  className?: string
}) => {
  return (
    <div
      className={clsx(
        'flex flex-col mb-1.5',
        className,
        match(spacing)
          .with('none', () => '')
          .with('tight', () => 'space-y-2')
          .with('normal', () => 'space-y-3')
          .with('loose', () => 'space-y-4')
          .otherwise(() => 'space-y-3')
      )}
    >
      {children}
    </div>
  )
}

export const CollapsibleContent = ({
  children,
  spacing = 'tight',
  className,
}: {
  children: React.ReactNode
  spacing?: 'tight' | 'normal' | 'loose' | 'none'
  className?: string
}) => {
  return (
    <RadixCollapsibleContent
      className={clsx(
        className,
        'flex flex-col',
        'data-[state=open]:mb-1.5',
        match(spacing)
          .with('none', () => '')
          .with('tight', () => 'space-y-2')
          .with('normal', () => 'space-y-3')
          .with('loose', () => 'space-y-4')
          .otherwise(() => 'space-y-3')
      )}
    >
      {children}
    </RadixCollapsibleContent>
  )
}

export const TextOption = ({
  name,
  id,
  value,
  mutate,
  placeholder,
  type = 'text',
  tooltipText,
  forceEditable,
  disabled,
  inputProps,
  fallbackInViewMode,
  horizontal = false,
  multiLine = false,
  className,
  titleAction,
  children,
  maxLength,
}: {
  name: string
  id: string
  value: string
  type?: string
  fallbackInViewMode?: string
  mutate: (newValue: string) => void
  placeholder?: string
  tooltipText?: string
  forceEditable?: boolean
  disabled?: boolean
  inputProps?: React.ComponentProps<typeof Input>
  horizontal?: boolean
  multiLine?: boolean
  className?: string
  titleAction?: {
    label: React.ReactNode
    onClick: (opts: { value: string; onChange: (v: string) => void }) => void
  }
  children?: React.ReactNode
  maxLength?: number
}) => {
  const {
    onChange,
    sync,
    valueRef: _valueRef,
    ...syncedInputProps
  } = useSyncedValue<HTMLInputElement>({
    value,
    customEventsKey: id,
    setValue: mutate,
  })
  const editorMode = useEditorMode()
  const viewMode = editorMode !== 'write:all' && !forceEditable

  if (viewMode && !syncedInputProps.value && !fallbackInViewMode) return null

  return (
    <Label
      htmlFor={id}
      label={
        <span
          className={clsx(
            'flex justify-between w-full items-baseline',
            !viewMode && 'min-w-[5.65rem]'
          )}
        >
          <span className="items-center flex">
            {name}
            {tooltipText && (
              <Tooltip delayDuration={500}>
                <TooltipTrigger asChild>
                  <InfoCircledIcon className="ml-1 text-dark-muted hover:text-dark-bright" />
                </TooltipTrigger>
                <TooltipContent>{tooltipText}</TooltipContent>
              </Tooltip>
            )}
          </span>
          {!disabled && titleAction && !viewMode && (
            <Button
              unstyled
              focusStyled
              className={clsx($labelButton(), 'font-normal cursor-default')}
              onClick={() => {
                titleAction.onClick({ value: syncedInputProps.value, onChange })
                const capitalized = title(syncedInputProps.value)
                onChange(capitalized)
              }}
            >
              {titleAction.label}
            </Button>
          )}
        </span>
      }
      size="xs"
      blocked={false}
      className={clsx(
        className,
        !horizontal && '[&>span]:!pb-0.5',
        multiLine && 'flex flex-col min-h-[50px]',
        viewMode && '[&>span]:!mr-0'
      )}
      horizontal={horizontal}
    >
      <div className="relative">
        {viewMode ? (
          <span className="leading-normal text-dark-bright">
            {syncedInputProps.value || fallbackInViewMode}
          </span>
        ) : (
          <>
            <Input
              asChild={multiLine}
              id={id}
              name={name}
              {...syncedInputProps}
              onBlur={sync}
              onChange={(e) => onChange(e.target.value)}
              autoComplete="off"
              type={type}
              size="xs"
              placeholder={placeholder}
              className={clsx(
                'block',
                disabled && 'cursor-not-allowed',
                multiLine && 'py-2 min-h-18'
              )}
              disabled={disabled}
              {...inputProps}
            >
              {multiLine ? (
                <AutoGrowingTextarea
                  maxLength={maxLength}
                  deps={[syncedInputProps.value]}
                />
              ) : null}
            </Input>
            {children}
          </>
        )}
      </div>
    </Label>
  )
}

const AutoGrowingTextarea = React.forwardRef<
  HTMLTextAreaElement,
  React.JSX.IntrinsicElements['textarea'] & {
    deps: React.DependencyList
    minHeight?: number
  }
>(({ deps, minHeight, ...props }, _ref) => {
  const ref = useTextAreaAutoGrow(deps, { minHeight })

  return (
    <textarea
      {...props}
      style={{
        resize: 'none',
        overflow: 'hidden',
        lineHeight: 1.35,
        ...props.style,
      }}
      // eslint-disable-next-line react-compiler/react-compiler
      ref={mergeRefs([_ref, ref])}
    />
  )
})

export const NumberOption = ({
  ...rest
}: {
  name: string
  id: string
  value: string
  mutate: (newValue: string) => void
  placeholder?: string
  inputProps?: React.ComponentProps<typeof Input>
  className?: string
}) => {
  return <TextOption type="number" {...rest} horizontal />
}

export const DateOption = ({
  name,
  tooltipText,
  value,
  mutate,
  time,
}: {
  name: string
  tooltipText?: string
  value: string
  mutate: (newValue: string) => void
  time: boolean
}) => {
  const id = React.useId()
  const editorMode = useEditorMode()
  const {
    onChange,
    sync: _,
    valueRef: __,
    ...syncedInputProps
  } = useSyncedValue<HTMLInputElement>({
    value,
    setValue: mutate,
  })

  const viewMode = editorMode !== 'write:all'
  if (viewMode && !syncedInputProps.value) return null

  return (
    <Label
      htmlFor={id}
      className={clsx('[&>span]:!pb-0.5', viewMode && '[&>span]:!mr-0')}
      label={
        <span className="inline-flex items-center">
          {name}
          {tooltipText && (
            <Tooltip delayDuration={500}>
              <TooltipTrigger asChild>
                <InfoCircledIcon className="ml-1 text-dark-muted hover:text-dark-bright" />
              </TooltipTrigger>
              <TooltipContent>{tooltipText}</TooltipContent>
            </Tooltip>
          )}
        </span>
      }
      blocked={false}
      size="xs"
      horizontal
    >
      {viewMode ? (
        <span className="text-dark-bright">{syncedInputProps.value}</span>
      ) : (
        <Input
          id={id}
          name={name}
          size="sm"
          type={time ? 'datetime-local' : 'date'}
          {...syncedInputProps}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
    </Label>
  )
}

export const BooleanOption = ({
  name,
  id,
  tooltipText,
  value,
  mutate,
  forceEditable,
  containerClassName,
  className,
  readOnlyClassName,
  disabled = false,
}: {
  name: string
  id: string
  tooltipText?: string
  value: boolean
  mutate: (newValue: boolean) => void
  forceEditable?: boolean
  disabled?: boolean
  containerClassName?: string
  className?: string
  readOnlyClassName?: string
}) => {
  const editorMode = useEditorMode()
  const viewMode = editorMode !== 'write:all' && !forceEditable

  if (viewMode && !value) return null

  return (
    <Label
      htmlFor={id}
      label={
        <span className={clsx('inline-flex items-center', className)}>
          {name}
          {tooltipText && (
            <Tooltip delayDuration={500}>
              <TooltipTrigger asChild>
                <InfoCircledIcon className="ml-1 text-dark-muted hover:text-dark-bright" />
              </TooltipTrigger>
              <TooltipContent>{tooltipText}</TooltipContent>
            </Tooltip>
          )}
        </span>
      }
      blocked={false}
      size="xs"
      className={clsx(
        '[&>input]:max-w-max',
        viewMode && ['[&>span]:!mr-0', readOnlyClassName],
        containerClassName
      )}
      horizontal
    >
      {viewMode ? (
        <CheckIcon height={15} width={15} className="text-dark-bright" />
      ) : (
        <Checkbox
          id={id}
          name={name}
          checked={value}
          onCheckedChange={(newValue) => mutate(Boolean(newValue))}
          disabled={disabled}
        />
      )}
    </Label>
  )
}

export const LinkOption = ({
  href,
  label,
  icon,
  onClick,
}: {
  href: string
  label: React.ReactNode
  icon?: React.ReactNode
  onClick?: LinkProps['onClick']
}) => {
  const isExternal = href.startsWith('http')
  return (
    <Link
      href={href}
      target={isExternal ? '_blank' : '_self'}
      className={clsx($labelButton(), 'inline-flex items-start')}
      onClick={onClick}
    >
      {icon && <span className="mr-2 mt-0.5">{icon}</span>}
      {label}
      {isExternal && <ExternalLinkIcon className="ml-3" />}
    </Link>
  )
}

export const $labelButton = cva(
  'text-dark-muted hover:text-dark-dim underline text-xs cursor-default'
)

export const ImageOrVideoOption = ({
  name,
  id,
  value,
  mutate,
  tooltipText,
  forceEditable,
  forceView,
  horizontal = false,
  disabled = false,
}: {
  name: string
  id: string
  value: null | { url: string; mimeType: string; width: number; height: number }
  forceView?: boolean
  mutate?: (
    value: null | {
      url: string
      mimeType: string
      width: number
      height: number
    }
  ) => void
  tooltipText?: string
  forceEditable?: boolean
  horizontal?: boolean
  disabled?: boolean
}) => {
  const { repoId, team } = useRepoReplicache().data ?? {}
  const teamId = team?.id
  const inputRef = React.useRef<HTMLInputElement>(null)
  const editorMode = useEditorMode()
  const [isDropping, setIsDropping] = React.useState(false)

  const { mutateAsync, isLoading: isLoadingUploadFile } =
    useUploadFileToRepoMutation()

  if (editorMode !== 'write:all' && !forceEditable) {
    if (!value && !forceView) return null
    return (
      <div
        id={id}
        className={clsx(
          'text-dark-bright text-xs flex grow',
          horizontal ? 'items-center justify-between' : 'flex-col gap-y-1.5'
        )}
      >
        <span className="text-dark-muted">{name}</span>
        <div>
          {value ? (
            <div
              style={{
                aspectRatio: `${value.width}/${value.height}`,
                maxHeight: 300,
              }}
              className="relative rounded-md overflow-hidden text-center text-xs text-dark-dim leading-4 w-full"
            >
              {VALID_IMAGE_MIME_TYPES.includes(value.mimeType) && (
                <img
                  src={value.url}
                  alt=""
                  className="w-full h-full absolute inset-0 object-cover pointer-events-none border border-dark-border-solid rounded-[inherit]"
                />
              )}
              {VALID_VIDEO_MIME_TYPES.includes(value.mimeType) && (
                <video
                  src={value.url}
                  className="w-full h-full absolute inset-0 object-cover pointer-events-none border border-dark-border-solid rounded-[inherit]"
                  autoPlay
                  muted
                  playsInline
                  loop
                />
              )}
            </div>
          ) : (
            'No preview'
          )}
        </div>
      </div>
    )
  }

  return (
    <Label
      htmlFor={id}
      label={
        <span className="inline-flex w-full justify-between">
          <span className="items-center inline-flex">
            {name}
            {tooltipText && (
              <Tooltip delayDuration={500}>
                <TooltipTrigger asChild>
                  <InfoCircledIcon className="ml-1 text-dark-muted hover:text-dark-bright" />
                </TooltipTrigger>
                <TooltipContent>{tooltipText}</TooltipContent>
              </Tooltip>
            )}
          </span>
          {Boolean(value) && !!mutate && (
            <span className="font-normal">
              <button
                className={$labelButton()}
                onClick={() => {
                  mutate(null)
                }}
              >
                Clear
              </button>
              {' · '}
              <button
                className={$labelButton()}
                onClick={() => {
                  inputRef.current?.click()
                }}
              >
                Replace
              </button>
            </span>
          )}
        </span>
      }
      size="xs"
      blocked={false}
      horizontal={horizontal}
    >
      <div
        className="relative rounded-md overflow-hidden text-center text-xs text-dark-dim leading-4"
        style={{
          aspectRatio: value ? `${value.width}/${value.height}` : '16/9',
          maxHeight: 300,
        }}
      >
        {mutate && (
          <Input
            ref={inputRef}
            id={id}
            name={name}
            onChange={async (e) => {
              if (!teamId || !repoId) return
              const file = e.target.files?.[0]

              if (!file) return

              if (
                !VALID_IMAGE_MIME_TYPES.includes(file.type) &&
                !VALID_VIDEO_MIME_TYPES.includes(file.type)
              ) {
                toast.error(`Invalid file type: ${file.type}`)
                return
              }

              if (
                file.size >
                10 * 1024 * 1024 // 10MB
              ) {
                toast.error('File size must be less than 10MB')
                return
              }

              let width: number | null = null
              let height: number | null = null

              let sizePromise: Promise<void> | null = null

              if (VALID_IMAGE_MIME_TYPES.includes(file.type)) {
                const img = new Image()
                img.src = URL.createObjectURL(file)
                sizePromise = new Promise((resolve) => {
                  img.onload = () => {
                    width = img.width
                    height = img.height
                    resolve()
                  }
                })
              } else if (VALID_VIDEO_MIME_TYPES.includes(file.type)) {
                const video = document.createElement('video')
                video.src = URL.createObjectURL(file)
                sizePromise = new Promise((resolve) => {
                  video.onloadedmetadata = () => {
                    width = video.videoWidth
                    height = video.videoHeight
                    resolve()
                  }
                })
              }

              try {
                const { uploadURL } = await mutateAsync({
                  file,
                  repoId,
                  teamId,
                })
                await sizePromise
                mutate({
                  url: uploadURL,
                  mimeType: file.type,
                  width: width ?? 16,
                  height: height ?? 9,
                })
              } catch (error) {
                console.error(error)
                toast.error('Failed to upload file')
              }
            }}
            autoComplete="off"
            disabled={disabled}
            type="file"
            className="absolute border-dashed text-opacity-0 file:hidden focus:border-solid h-full w-full inset-0"
            onDragEnter={(e) => {
              e.stopPropagation()
              if (!e.dataTransfer.types.includes('Files')) return
              setIsDropping(true)
            }}
            onDragLeave={(e) => {
              e.stopPropagation()
              setIsDropping(false)
            }}
            onDrop={() => setIsDropping(false)}
            accept={[...VALID_IMAGE_MIME_TYPES, VALID_VIDEO_MIME_TYPES].join(
              ','
            )}
          />
        )}
        {value ? (
          <>
            {VALID_IMAGE_MIME_TYPES.includes(value.mimeType) && (
              <img
                src={value.url}
                alt=""
                className="w-full h-full absolute inset-0 object-cover pointer-events-none border border-dark-border-solid rounded-[inherit]"
              />
            )}
            {VALID_VIDEO_MIME_TYPES.includes(value.mimeType) && (
              <video
                src={value.url}
                className="w-full h-full absolute inset-0 object-cover pointer-events-none border border-dark-border-solid rounded-[inherit]"
                autoPlay
                muted
                playsInline
                loop
              />
            )}
            <div
              className={clsx(
                'opacity-0 transition-opacity absolute text-dark-muted pointer-events-none p-4 inset-0 flex items-center justify-center w-full h-full bg-dark-shade/80',
                (isDropping || isLoadingUploadFile) && 'opacity-100'
              )}
            >
              {isLoadingUploadFile && <p>Uploading...</p>}
              {isDropping && <p>Drop to upload</p>}
            </div>
          </>
        ) : (
          <div className="absolute text-dark-muted pointer-events-none inset-0 flex items-center justify-center w-full h-full">
            <p className="p-4">
              {isLoadingUploadFile
                ? 'Uploading...'
                : isDropping
                  ? 'Drop to upload'
                  : 'Upload an image or a video'}
            </p>
          </div>
        )}
      </div>
    </Label>
  )
}

export const CodeOption = ({ code }: Pick<CodeSnippetProps, 'code'>) => {
  return <CodeSnippet code={code} language="graphql" />
}

export const MultipleCheckboxOption = ({
  title,
  id,
  options,
  checkedOptions,
  labelModifier,
  disabled = false,
  mutate,
}: {
  title: string
  id: string
  options: string[]
  checkedOptions: string[] | []
  labelModifier?: (option: string) => string
  disabled?: boolean
  mutate: (option: string, newValue: boolean) => void
}) => {
  const editorMode = useEditorMode()

  return (
    <div className="flex flex-col gap-2">
      <p className="text-dark-muted text-sm">{title}</p>
      <div className="flex flex-wrap gap-x-4 gap-y-2" tabIndex={0} id={id}>
        {editorMode !== 'write:all'
          ? options.map((option) => {
              const isChecked = checkedOptions?.some(
                (checkedOption) => checkedOption === option
              )
              if (!isChecked) return null
              return <div key={option}>{option}</div>
            })
          : options.map((option) => {
              const isChecked = checkedOptions?.some(
                (checkedOption) => checkedOption === option
              )
              return (
                <div key={option}>
                  <Label
                    htmlFor={option}
                    label={labelModifier ? labelModifier(option) : option}
                    blocked={false}
                    className="[&>span]:pr-1.5"
                    size="xs"
                    horizontal
                  >
                    <Checkbox
                      id={option}
                      name={option}
                      checked={isChecked}
                      onCheckedChange={(newValue) =>
                        mutate(option, Boolean(newValue))
                      }
                      disabled={disabled}
                    />
                  </Label>
                </div>
              )
            })}
      </div>
    </div>
  )
}

export const List: React.FC<{
  title: string
  id: string
  options: { name: string; value: string; selected?: boolean }[]
  mutate: (newOptions: { name: string; value: string }[]) => void
  mutateRenameOption: (data: {
    index: number
    name: string
    value: string
  }) => void
  mutateDeleteOption: (data: {
    index: number
    name: string
    value: string
  }) => void
  editable?: boolean
}> = ({
  title,
  id,
  options,
  mutate,
  mutateRenameOption,
  mutateDeleteOption,
  editable = false,
}) => {
  const editorMode = useEditorMode()

  const [isCollapsed, setIsCollapsed] = React.useState(true)
  const [editingIndex, setEditingIndex] = React.useState<number | null>(null)
  const inputRef = React.useRef<HTMLInputElement>(null)
  const listRef = React.useRef<HTMLUListElement>(null)
  const newValueToggleState = useToggleState()

  React.useEffect(() => {
    if (!editingIndex === null || !inputRef.current) return

    inputRef.current.select()
  }, [editingIndex])

  const shouldBeCollapsible = options.length > 6

  const flipBack = () => {
    if (!listRef.current) return newValueToggleState.handleOff()
    flip(listRef.current, newValueToggleState.handleToggle, {
      duration: 0.07,
      ease: 'power2.inOut',
    })
  }

  return (
    <div className="flex flex-col gap-1.5" id={id} tabIndex={0}>
      <div className="flex items-center justify-between">
        <p className="text-dark-muted text-sm">
          {title}&nbsp;({options.length})
        </p>
        <ConfigureContainer block={null}>
          <IconButton
            disabled={!editable}
            onClick={() => {
              if (!listRef.current) return newValueToggleState.handleToggle()

              flip(listRef.current, newValueToggleState.handleToggle, {
                duration: 0.07,
                ease: 'power2.inOut',
              })
            }}
            className="flex ml-auto items-center text-xs font-normal text-dark-muted"
          >
            <PlusIcon width={12} height={12} />
          </IconButton>
        </ConfigureContainer>
      </div>

      {(Boolean(options.length) || newValueToggleState.isOn) && (
        <div
          style={isCollapsed && shouldBeCollapsible ? { maxHeight: 172 } : {}}
          className={clsx(
            shouldBeCollapsible && 'pb-7',
            'relative overflow-hidden pl-2'
          )}
        >
          {editable && newValueToggleState.isOn && (
            <Input
              ref={inputRef}
              size="xs"
              className="!h-[23px] !px-1 -ml-1 w-[calc(100%+4px)]"
              autoFocus
              type="text"
              onBlur={flipBack}
              onKeyDown={(e) => {
                if (e.key !== 'Escape' && e.key !== 'Enter') return

                if (e.key === 'Enter') {
                  const newVal = e.currentTarget.value.trimEnd()
                  if (newVal) {
                    mutate([{ name: newVal, value: newVal }, ...options])
                  }
                }

                flipBack()
              }}
            />
          )}

          <ul ref={listRef}>
            {options.map((option, index) => {
              const isEditing = editingIndex === index

              return (
                <li
                  key={option.value}
                  className="flex items-center justify-between"
                >
                  <div className="relative -ml-[3px] flex-1 overflow-hidden py-0.5 pl-1 pr-1.5">
                    <span
                      className={clsx(
                        'block truncate text-xs',
                        option.selected
                          ? 'text-dark-control-highlight'
                          : 'text-dark-muted'
                      )}
                      onDoubleClick={() => {
                        if (editorMode !== 'write:all') return
                        setEditingIndex(index)
                      }}
                    >
                      {option.name}
                    </span>
                    {isEditing && (
                      <Input
                        ref={inputRef}
                        size="xs"
                        className="absolute left-0 top-1/2 !h-[23px] w-[calc(100%-theme(spacing.1))] -translate-y-1/2 !px-1"
                        type="text"
                        onBlur={(e) => {
                          const newVal = (
                            e.target as HTMLInputElement
                          ).value.trimEnd()
                          if (!newVal) return mutate(options)

                          mutateRenameOption({
                            index,
                            name: newVal,
                            value: newVal,
                          })
                          setEditingIndex(null)
                        }}
                        onKeyDown={(e) => {
                          if (e.key !== 'Enter' && e.key !== 'Escape') return
                          ;(e.target as HTMLInputElement).blur()
                        }}
                        defaultValue={option.value}
                        autoFocus
                      />
                    )}
                  </div>

                  <ConfigureContainer block={null}>
                    <div className="flex shrink-0">
                      <IconButton
                        disabled={editorMode !== 'write:all'}
                        className="text-dark-muted"
                        onClick={() => {
                          if (editorMode !== 'write:all') return
                          setEditingIndex(index)
                        }}
                      >
                        <Pencil1Icon width={12} height={12} />
                      </IconButton>
                      <IconButton
                        disabled={editorMode !== 'write:all'}
                        className="text-dark-muted"
                        onClick={() => {
                          if (editorMode !== 'write:all') return
                          mutateDeleteOption({
                            index,
                            name: option.name,
                            value: option.value,
                          })
                        }}
                      >
                        <Cross1Icon width={12} height={12} />
                      </IconButton>
                    </div>
                  </ConfigureContainer>
                </li>
              )
            })}
          </ul>

          {shouldBeCollapsible && (
            <>
              {isCollapsed && (
                <div className="from-dark-bg bottom-6 pointer-events-none absolute left-1 h-8 w-[calc(100%-theme(spacing.2))] bg-gradient-to-t to-transparent" />
              )}
              <div className="bottom-0 bg-dark-bg absolute left-0 flex w-full p-1">
                <IconButton
                  className="w-full rounded-md"
                  onClick={() => setIsCollapsed((prev) => !prev)}
                >
                  <ChevronDownIcon
                    className={clsx(!isCollapsed && 'rotate-180')}
                  />
                </IconButton>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}

export type CheckItem = {
  name: string
  id: Block['id']
  href?: string
  icon?: React.ReactNode
  disabled?: boolean
  disabledReason?: string
}

export const CheckList = ({
  title,
  id,
  options,
  checkedOptions,
  mutate,
  listClassName,
  itemName,
  className,
  popoverConfig,
  addNew,
  labelClassName,
  ...dndStuff
}: {
  title: string
  id: string
  options: Array<CheckItem>
  checkedOptions: Array<CheckItem>
  mutate: (mode: boolean | 'overwrite', options: CheckItem[]) => void
  listClassName?: string
  itemName?: string
  addNew?: {
    label: string
    onClick: () => void
  }
  className?: string
  labelClassName?: string
  popoverConfig?: Partial<{
    prefix: string
    allowedLabel: string
    notAllowedLabel: string
  }>
} & (
  | {
      dnd?: false
      dndItemList?: never
      onDragEnd?: never
    }
  | {
      dnd: true
      dndItemList: Array<string>
      onDragEnd: (e: DragEndEvent) => void
    }
)) => {
  const editorMode = useEditorMode()

  if (editorMode !== 'write:all' && !checkedOptions.length) return null

  return (
    <CheckListPopover
      itemName={itemName ?? title}
      title={title}
      options={options}
      checkedOptions={checkedOptions}
      mutate={mutate}
      {...popoverConfig}
      {...dndStuff}
    >
      <div className={clsx('flex flex-col gap-1', className)}>
        <div className="flex items-baseline gap-x-2 justify-between whitespace-nowrap">
          <Label
            size="xs"
            horizontal
            label={title}
            blocked={false}
            className={labelClassName}
          />
          {editorMode === 'write:all' && Boolean(options.length) && (
            <PopoverTrigger asChild>
              <Button
                unstyled
                focusStyled
                className="text-xs font-medium leading-normal text-dark-control-base hover:text-dark-control-highlight cursor-default"
                id={id}
              >
                Edit
              </Button>
            </PopoverTrigger>
          )}
        </div>
        {Boolean(checkedOptions.length) || addNew ? (
          <ScrollArea asChild>
            <ul
              className={clsx(
                'text-dark-faint text-xs gap-y-1 flex flex-col',
                listClassName
              )}
            >
              {checkedOptions.map((option) => {
                const Wrapper = option.href ? Link : 'span'

                return (
                  <Wrapper
                    key={option.id}
                    href={option.href as string}
                    className={clsx(
                      option?.href && 'hover:text-dark-dim',
                      'rounded-md outline-1 outline-offset-2 outline-dark-control-base focus-visible:outline cursor-default'
                    )}
                  >
                    <li id={option.id} className="flex gap-x-1.5 items-center">
                      {option.icon}

                      <span className="truncate">{option.name}</span>
                    </li>
                  </Wrapper>
                )
              })}
              {addNew && editorMode === 'write:all' && (
                <Button
                  unstyled
                  onClick={addNew.onClick}
                  className="rounded-md outline-1 outline-offset-2 outline-dark-control-base focus-visible:outline cursor-default text-dark-muted hover:text-dark-bright mt-1"
                >
                  <li className="flex gap-x-1.5 items-center font-medium">
                    <PlusIcon />

                    <span>{addNew.label}</span>
                  </li>
                </Button>
              )}
            </ul>
          </ScrollArea>
        ) : (
          <span className="text-dark-faint text-xs leading-normal">
            Selected options will show up here.
          </span>
        )}
      </div>
    </CheckListPopover>
  )
}

type CheckListPopoverProps = {
  children: React.ReactNode
  itemName: string
  title: string
  prefix?: string
  allowedLabel?: string
  notAllowedLabel?: string
  options: Array<CheckItem>
  checkedOptions: Array<CheckItem>
  mutate: (newValue: boolean | 'overwrite', options: CheckItem[]) => void
  footer?: React.ReactNode
  emptyState?: (search: string) => React.ReactNode
  filterById?: boolean
} & (
  | {
      dnd?: false
      dndItemList?: never
      onDragEnd?: never
    }
  | {
      dnd: true
      dndItemList: Array<string>
      onDragEnd: (e: DragEndEvent) => void
    }
)

const CheckListPopover = ({
  children,
  itemName,
  prefix = 'Edit',
  allowedLabel = 'Allowed',
  notAllowedLabel = 'Not allowed',
  title,
  options,
  checkedOptions,
  mutate,
  footer,
  emptyState,
  filterById,
  ...dndStuff
}: CheckListPopoverProps) => {
  const [filter, setFilter] = React.useState('')

  const filteredCheckedOptions = React.useMemo(() => {
    if (!filter) return checkedOptions
    return checkedOptions.filter(
      (option) =>
        option.name.toLowerCase().includes(filter.toLowerCase().trim()) ||
        (filterById &&
          option.id.toLowerCase().includes(filter.toLowerCase().trim()))
    )
  }, [checkedOptions, filter, filterById])

  const filteredUncheckedOptions = React.useMemo(() => {
    return options.filter((option) => {
      const isChecked = checkedOptions.some((opt) => opt.id === option.id)
      if (isChecked) return false
      if (!filter) return true
      return option.name.toLowerCase().includes(filter.toLowerCase().trim())
    })
  }, [options, checkedOptions, filter])

  const noResults = React.useMemo(
    () =>
      filteredCheckedOptions.length === 0 &&
      filteredUncheckedOptions.length === 0,
    [filteredCheckedOptions, filteredUncheckedOptions]
  )

  const editorMode = useEditorMode()
  const readOnly = editorMode !== 'write:all'

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const clearAllVisible = React.useCallback(
    () =>
      filter ? mutate(false, filteredCheckedOptions) : mutate('overwrite', []),
    [filter, filteredCheckedOptions, mutate]
  )

  const selectAllVisible = React.useCallback(
    () =>
      filter
        ? mutate(true, filteredUncheckedOptions)
        : mutate(
            'overwrite',
            options.filter((opt) => opt.disabled !== true)
          ),
    [filter, filteredUncheckedOptions, mutate, options]
  )

  return (
    <Popover modal>
      <PopoverAnchor asChild>{children}</PopoverAnchor>
      <PopoverContent
        sideOffset={20}
        side="left"
        collisionPadding={{ bottom: 32 }}
        align="start"
        className="max-h-[460px] grid grid-rows-[auto_1fr] !p-0 w-[240px]"
      >
        <div className="grid grid-cols-[1fr_auto] items-center gap-x-1.5 gap-y-2 grid-rows-[auto_auto] px-4 py-3 ">
          <p className="text-dark-bright font-medium text-sm leading-normal">
            {prefix} {title.toLowerCase()}
          </p>
          <PopoverClose asChild>
            <IconButton
              aria-label="Close"
              className="-mr-1 text-dark-faint hover:text-dark-bright hover:!bg-transparent"
            >
              <Cross2Icon />
            </IconButton>
          </PopoverClose>
          <Input
            size="sm"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder={`Search ${itemName.toLowerCase()}`}
            autoFocus
            icon={
              <MagnifyingGlassIcon
                className={clsx(!filter && 'text-dark-faint')}
              />
            }
            containerClassName="w-full col-span-full"
          />
        </div>
        <ScrollArea>
          {noResults ? (
            emptyState ? (
              emptyState(filter)
            ) : (
              <div
                className={clsx(
                  'px-4 text-xs font-medium text-dark-muted leading-normal',
                  footer ? 'pb-2' : 'pb-3'
                )}
              >
                No results
              </div>
            )
          ) : (
            <ul
              className={clsx(
                'px-4 flex flex-col gap-1',
                footer ? 'pb-2' : 'pb-4'
              )}
            >
              <div className="flex gap-1 items-center justify-between text-xs font-medium text-dark-faint">
                <span>{allowedLabel}</span>
                {readOnly || (
                  <Button
                    unstyled
                    focusStyled
                    className="text-dark-dim hover:text-dark-bright cursor-default -outline-offset-1 px-1 -mr-1"
                    onClick={clearAllVisible}
                  >
                    Clear all
                  </Button>
                )}
              </div>
              <DndContext
                sensors={sensors}
                autoScroll={false}
                onDragEnd={dndStuff?.onDragEnd ?? (() => {})}
              >
                <SortableContext
                  items={dndStuff?.dndItemList ?? []}
                  strategy={verticalListSortingStrategy}
                  disabled={!readOnly || !dndStuff?.dnd}
                >
                  {filteredCheckedOptions.map((option) => {
                    return (
                      <CheckItem
                        key={option.id}
                        item={option}
                        checked
                        dnd={dndStuff?.dnd}
                        onCheckedChange={(checked: CheckedState) =>
                          mutate(Boolean(checked), [option])
                        }
                      />
                    )
                  })}
                </SortableContext>
              </DndContext>

              {readOnly || (
                <>
                  <hr className="order-2 border-dark-border-solid mb-1.5 mt-[0.3125rem]" />
                  <div className="order-2 flex gap-1 items-center justify-between text-xs font-medium text-dark-faint">
                    <span>{notAllowedLabel}</span>
                    <Button
                      unstyled
                      focusStyled
                      className="text-dark-dim hover:text-dark-bright cursor-default -outline-offset-1 px-1 -mr-1"
                      onClick={selectAllVisible}
                    >
                      Select all
                    </Button>
                  </div>
                  {filteredUncheckedOptions.map((option) => {
                    return (
                      <CheckItem
                        key={option.id}
                        item={option}
                        checked={false}
                        onCheckedChange={(checked: CheckedState) =>
                          mutate(Boolean(checked), [option])
                        }
                      />
                    )
                  })}
                </>
              )}
            </ul>
          )}
        </ScrollArea>
        {footer}
      </PopoverContent>
    </Popover>
  )
}

const CheckItem = ({
  item,
  checked,
  dnd,
  onCheckedChange,
}: {
  item: CheckItem
  checked: boolean
  dnd?: boolean
  onCheckedChange: (checked: CheckedState) => void
}) => {
  const { attributes, listeners, setNodeRef, transform } = useSortable({
    id: item.id,
  })
  const translate = { transform: CSS.Transform.toString(transform) }
  const editorMode = useEditorMode()
  const readonly = editorMode !== 'write:all'

  if (item.disabled && item.disabledReason) {
    return (
      <SimpleTooltip
        contentClassName="first-letter:uppercase"
        content={item.disabledReason}
      >
        <span
          className={clsx(
            'opacity-50 cursor-default text-dark-muted flex gap-x-2 text-xs items-center [&>span]:min-w-0 [&>span]:truncate overflow-hidden',
            checked ? 'order-1' : 'order-3'
          )}
        >
          {item.icon}
          <span>{item.name}</span>
        </span>
      </SimpleTooltip>
    )
  }

  return (
    <li
      className={clsx(
        'flex items-center group/check-item relative',
        checked || 'order-2'
      )}
      ref={setNodeRef}
      style={translate}
    >
      {!readonly && dnd && (
        <DragHandleDots2Icon
          width={15}
          height={15}
          className="text-dark-faint shrink-0 cursor-grab inline mr-1.5 outline-dark-control-base"
          {...listeners}
          {...attributes}
        />
      )}
      <LabeledCheckbox
        title={item.name}
        icon={item.icon}
        label={item.name}
        aria-disabled={item.disabled}
        blocked={false}
        name={item.name}
        className="[&>span]:min-w-0 [&>span]:truncate overflow-hidden"
        horizontal
        checkboxProps={{
          'aria-disabled': item.disabled,
          checked,
          disabled: readonly || item.disabled,
          onCheckedChange,
        }}
        actions={
          item.href
            ? [
                <LinkToBlock
                  key="link-to-block"
                  href={item.href}
                  className="hidden group-hover/check-item:flex"
                />,
              ]
            : []
        }
      />
    </li>
  )
}

const LinkToBlock = ({
  className,
  href,
  ...props
}: {
  href: string
} & React.HTMLAttributes<HTMLAnchorElement>) => {
  return (
    <Tooltip delayDuration={100}>
      <TooltipTrigger asChild>
        <Link
          href={href}
          onClick={(e) => e.stopPropagation()}
          className={clsx(
            'cursor-default rounded-sm p-0.5 hover:bg-dark-elevated text-dark-muted hover:text-dark-dim bg-dark-base',
            className
          )}
          {...props}
          aria-label="Open block"
        >
          <EnterIcon className="size-3.5" />
        </Link>
      </TooltipTrigger>
      <TooltipContent className="py-1 !px-2">Open block</TooltipContent>
    </Tooltip>
  )
}

export const LayoutOption = <
  T extends {
    id: string
    title: string
    icon?: React.ReactNode
    description?: string
  },
>({
  title,
  id,
  options,
  value,
  onValueChange,
  direction = 'horizontal',
}: {
  title?: string
  id: string
  options: T[]
  value: T['id']
  onValueChange: (value: T) => void | Promise<void>
  direction?: 'horizontal' | 'vertical'
}) => {
  const editorMode = useEditorMode()
  const disabled = editorMode !== 'write:all'

  return (
    <div>
      {title && (
        <p
          className={clsx(
            disabled ? 'text-dark-bright' : 'text-dark-muted',
            'text-xs pb-1.5 font-medium'
          )}
        >
          {title}
        </p>
      )}

      <div
        className={clsx(
          'grid gap-1.5',
          direction === 'horizontal' && 'grid-cols-3'
        )}
        id={id}
        tabIndex={0}
      >
        {options.map((option) => {
          return (
            <Button
              unstyled
              focusStyled
              key={option.id}
              disabled={disabled}
              onClick={() => {
                if (disabled) return

                onValueChange(option)
              }}
              className={clsx(
                'rounded border cursor-default border-dark-elevated bg-dark-shade flex items-center justify-center text-xs leading-none flex-col gap-x-2 gap-y-0.5',
                direction === 'horizontal' ? 'pt-2 pb-1 px-1' : 'p-2 text-left',
                {
                  'hover:bg-dark-shade-hover': !disabled,
                  'cursor-not-allowed': disabled,
                  'opacity-50': value !== option.id,
                  'hover:opacity-100': value !== option.id && !disabled,
                  '!grid grid-cols-[auto_1fr] [>:first-child]:row-span-full':
                    Boolean(option.description && option.icon),
                }
              )}
            >
              {option.icon}
              <span className="leading-normal font-medium">{option.title}</span>
              {option.description && (
                <span className="text-dark-muted col-start-2">
                  {option.description}
                </span>
              )}
            </Button>
          )
        })}
      </div>
    </div>
  )
}

export const AllowedItemsOption = ({
  handleChange,
  handleAddOption,
  handleClear,
  handleSelectAll,
  options,
  title,
  id,
}: {
  title: string
  id: string
  handleAddOption?: (name: string) => void
  handleChange: (name: string, enabled: boolean) => void
  handleClear: VoidFunction
  handleSelectAll: VoidFunction
  options: {
    name: string
    icon: React.ReactNode
    allowed: boolean
    tooltip?: string
  }[]
}) => {
  const editorMode = useEditorMode()
  const disabled = editorMode !== 'write:all'

  const filteredOptions = React.useMemo(() => {
    if (disabled) {
      return options.filter((option) => option.allowed)
    }
    return options
  }, [options, disabled])

  return (
    <div className="flex flex-col items-start gap-2" id={id} tabIndex={0}>
      <div className="flex gap-1.5 items-baseline w-full mt-2">
        <p
          className={clsx(
            disabled ? 'text-dark-bright' : 'text-dark-muted',
            'text-xs font-medium mr-auto'
          )}
        >
          {title}
        </p>
        {Boolean(!disabled) && (
          <>
            <Button
              onClick={handleClear}
              unstyled
              focusStyled
              className="text-dark-faint hover:text-dark-dim cursor-default font-medium leading-normal text-xs"
            >
              Clear
            </Button>
            <Button
              onClick={handleSelectAll}
              aria-label="Select all"
              unstyled
              focusStyled
              className="text-dark-control-base hover:text-dark-control-highlight cursor-default font-medium leading-normal text-xs"
            >
              All
            </Button>
          </>
        )}
      </div>

      {filteredOptions.length ? (
        <div className="flex flex-1 flex-wrap gap-2 justify-stretch w-full">
          {filteredOptions.map((option) => {
            return (
              <Tooltip key={option.name}>
                <TooltipTrigger asChild>
                  <Button
                    unstyled
                    focusStyled
                    data-option-name={option.name}
                    disabled={disabled}
                    name={`allowed-block-${option.name}`}
                    className={clsx(
                      'border-dark-border-solid text-dark-bright enabled:hover:bg-dark-shade inline-flex size-8 shrink-0 grow max-w-[min(17%,_38px)] cursor-default items-center justify-center border p-1.5 text-xs leading-none transition-opacity',
                      {
                        '!cursor-default': disabled,
                        'bg-dark-base': option.allowed,
                        'opacity-30 bg-dark-shade hover:!bg-dark-base hover:opacity-100':
                          !option.allowed,
                      }
                    )}
                    onClick={() => handleChange(option.name, !option.allowed)}
                  >
                    {option.icon}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {option.tooltip ?? capitalCase(option.name)}
                </TooltipContent>
              </Tooltip>
            )
          })}
        </div>
      ) : (
        <p className="text-dark-muted text-xs">No options available</p>
      )}
      {handleAddOption && !disabled && (
        <Button>
          <PlusIcon />
        </Button>
      )}
    </div>
  )
}

export const MaxSizeOption = ({
  mutate,
  size,
  id,
}: {
  mutate: (newValue: string, unit: MaxSizeUnit) => void
  size: { value?: string; unit: MaxSizeUnit }
  id: string
}) => {
  const sizes: MaxSizeUnit[] = ['kb', 'mb', 'gb']
  const editorMode = useEditorMode()

  if (editorMode !== 'write:all' && !size.value) return null

  return (
    <div
      className={clsx(
        'flex items-center',
        editorMode === 'write:all' ? 'gap-2' : 'gap-0.5'
      )}
    >
      <NumberOption
        className={clsx(editorMode !== 'write:all' && '!w-fit')}
        value={size.value || ''}
        placeholder={size.value || '-'}
        name="Max size"
        id={id}
        inputProps={{ min: 1 }}
        mutate={(value) => {
          if (parseInt(value) < 0) return
          return mutate(value, size.unit)
        }}
      />
      <ConfigureContainer block={null}>
        <Select
          disabled={editorMode !== 'write:all'}
          value={size.unit}
          onValueChange={(unit) => {
            const defaultSize =
              unit === 'kb' ? '300' : unit === 'mb' ? '5' : '1'
            mutate(size.value || defaultSize, unit as MaxSizeUnit)
          }}
        >
          <SelectTrigger>
            <SelectValue>{size.unit}</SelectValue>
          </SelectTrigger>
          <SelectContent>
            {sizes.map((s) => (
              <SelectItem value={s} key={s} className="text-xs">
                <span>{s}</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </ConfigureContainer>
      <ViewContainer>
        <span>{size.unit}</span>
      </ViewContainer>
    </div>
  )
}

export type PropertiesPanelComboboxOption = ComboboxOption & {
  addNew?: boolean
  icon?: React.ReactNode
  preSeparator?: boolean
  postSeparator?: boolean
}

type SelectorOptionProps<OptionType extends PropertiesPanelComboboxOption> = {
  renderResult: SelectorProps<OptionType>['renderResult']
  options: SelectorProps<OptionType>['options']
  onSelect: SelectorProps<OptionType>['onSelect']
  onRemove?: SelectorProps<OptionType>['onRemove']
  selected: OptionType | OptionType[] | undefined
  emptyStateLabel: string
  id: string
}

export const SelectorOption = <
  OptionType extends PropertiesPanelComboboxOption,
>({
  renderResult,
  options,
  onSelect,
  selected,
  emptyStateLabel,
  onRemove,
  id,
}: SelectorOptionProps<OptionType>) => {
  const editorMode = useEditorMode()
  const disabled = editorMode !== 'write:all'
  const [search, setSearch] = React.useState('')

  if (disabled) {
    if (Array.isArray(selected)) {
      if (Boolean(selected.length)) {
        return (
          <p className="text-sm leading-5">
            {selected.map((s) => s.label).join(',')}
          </p>
        )
      }

      return (
        <p className="text-sm leading-5 font-medium text-dark-muted">
          No option selected
        </p>
      )
    }

    if (Boolean(selected)) {
      return (
        <p className="text-sm leading-5">
          {selected?.label ?? selected?.value}
        </p>
      )
    }

    return (
      <p className="text-sm leading-5 font-medium text-dark-muted">
        No option selected
      </p>
    )
  }

  return (
    <Selector
      onSelect={onSelect}
      options={options}
      search={search}
      setSearch={setSearch}
      renderResult={renderResult}
      onRemove={onRemove}
      disabled={disabled}
      icon={<CaretSortIcon className="text-dark-muted size-4" />}
      id={id}
      size="sm"
    >
      {({ options, getOptionProps, selectedIndex }) => {
        if (!options.length) {
          return <SelectorEmptyState>{emptyStateLabel}</SelectorEmptyState>
        }

        return options.map((option, index) => {
          const isHighlighted = selectedIndex === index
          const isSelected = Array.isArray(selected)
            ? selected.map((s) => s.value).includes(option.value)
            : selected?.value === option.value
          const isDisabled = option.disabled

          return (
            <div key={index}>
              {option.preSeparator && (
                <hr className="border-dark-border-solid my-1" />
              )}
              <SelectorItem
                key={index}
                {...getOptionProps(index)}
                className={clsx(
                  isHighlighted && 'bg-dark-shade outline-none',
                  isSelected && 'text-dark-control-highlight font-medium',
                  'relative flex h-8 cursor-default select-none scroll-mt-1 items-center truncate rounded-md px-3 py-1.5 text-xs leading-none outline-none disabled:pointer-events-none disabled:opacity-30',
                  isDisabled && 'opacity-30'
                )}
              >
                <span className="relative">
                  {option.icon ?? (
                    <BlockIcon
                      block={{ type: 'component' }}
                      iconProps={{
                        className: isSelected
                          ? 'text-dark-control-highlight'
                          : 'text-dark-muted',
                      }}
                    />
                  )}
                  {option.addNew && (
                    <span className="absolute -bottom-0.5 -right-1">
                      <ReferencePlusIcon
                        className="size-3"
                        borderColor={
                          isHighlighted
                            ? 'var(--color-elevated)'
                            : 'var(--color-shade)'
                        }
                      />
                    </span>
                  )}
                </span>
                <span className="ml-2 inline-flex w-full items-center justify-between">
                  <span>{option.label}</span>
                </span>
              </SelectorItem>
              {option.postSeparator && (
                <hr className="border-dark-border-solid my-1" />
              )}
            </div>
          )
        })
      }}
    </Selector>
  )
}

export const SelectOption = ({
  title,
  id,
  value,
  placeholder,
  options,
  handleValueChange,
  horizontal = false,
  labelClassName,
  forceEditable,
  disabled: _disabled,
}: {
  title?: string
  value?: string
  id: string
  placeholder?: string
  options: { id: string; label: string; icon?: React.ReactNode }[]
  handleValueChange: (value: string) => void
  horizontal?: boolean
  labelClassName?: string
  forceEditable?: boolean
  disabled?: boolean
}) => {
  const editorMode = useEditorMode()
  const disabled = (editorMode !== 'write:all' && !forceEditable) || _disabled
  const selected = options.find((o) => o.id === value)

  if (horizontal && disabled) {
    return selected ? (
      <div className="flex gap-x-2 items-center">
        <p className={clsx('text-sm text-dark-muted', labelClassName)}>
          {title}
        </p>
        <p className="flex items-center gap-x-1.5 text-xs text-dark-bright leading-normal">
          {selected?.icon}
          {selected?.label}
        </p>
      </div>
    ) : null
  }

  if (editorMode === 'read') return null

  return (
    <div
      className={clsx(
        'flex',
        horizontal ? 'gap-x-2 items-center' : 'flex-col gap-y-1.5'
      )}
    >
      <p
        className={clsx(
          disabled ? 'text-dark-bright' : 'text-dark-muted',
          'text-sm',
          labelClassName
        )}
      >
        {title}
      </p>

      <Select
        value={options.find((opt) => opt.id === value)?.id}
        onValueChange={handleValueChange}
        disabled={disabled}
      >
        <SelectTrigger
          className={clsx(horizontal && 'grow')}
          aria-label="language"
          size="small"
          id={id}
          iconRight
        >
          <SelectValue placeholder={placeholder}>
            <span className="flex gap-x-1 items-center">
              {selected?.icon}
              {selected?.label}
            </span>
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem
              key={option.id}
              value={option.id}
              className="[&>:last-child]:flex [&>:last-child]:items-center [&>:last-child]:gap-x-1.5"
              indicator={
                <span className="absolute left-1.5 flex size-[0.9375rem] items-center justify-center">
                  <ItemIndicator>
                    <CheckIcon className="size-[0.9375rem]" />
                  </ItemIndicator>
                </span>
              }
            >
              {option.icon}
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}

export type BlockTitleGroupProps = {
  block: Block
  path: string
  children?: React.ReactNode
  forceEditable?: boolean
}

export const BlockTitleGroup = ({
  block,
  path,
  children,
  forceEditable,
}: BlockTitleGroupProps) => {
  const { data } = useRepoReplicache()
  const replicache = data?.replicache
  const blockTreeManager = useWorkingTreeStore((s) => s.tree)
  const undoManager = useRepoUndoManager()
  const sourceBlockId = idToSourceBlockId(block)
  const activeTabKey = useTabsStore((s) => s.activeTab?.key)
  const editorMode = useEditorMode()

  const nodeBlock = blockTreeManager?.getNode(block.id, {
    fallbackKey: 'sourceBlockId',
  })
  const parent = nodeBlock?.parentKey
    ? blockTreeManager?.getNode(nodeBlock.parentKey, {
        fallbackKey: 'sourceBlockId',
      })
    : null

  const getInputFocusOp = React.useCallback(
    (id: string) => {
      invariant(parent?.value?.id, 'parent.value.id is required')
      return getBlockFocusOp({
        block: {
          id: block.id,
          sourceBlockId: block.sourceBlockId,
        },
        preferredTabKey: activeTabKey,
        focusType: 'block:properties-panel',
        elementIdOverride: id,
      })
    },
    [activeTabKey, block.id, block.sourceBlockId, parent?.value?.id]
  )

  const selectedDashboardVariants = useSelectedVariants({
    parentBlock: block,
  })

  const autoGeneratedValues =
    useTreeSlice(blockTreeManager, (tm) => {
      if (!blockTreeManager || !nodeBlock) return {}
      return getApiNameAndSlug({
        nodeBlock,
        treeManager: {
          getNode: tm.getNode,
          getChildrenKeys: tm.getChildrenKeys,
        },
      })
    }) || {}

  const hasDescription = Boolean(block.description)
  const [isDescriptionOpen, setDescriptionOpen] = React.useState(hasDescription)
  const {
    onChange,
    sync,
    valueRef: _valueRef,
    ...syncedDescriptionProps
  } = useSyncedValue<HTMLTextAreaElement>({
    value: block.description ?? '',
    customEventsKey: `${sourceBlockId}-pp-description`,
    setValue: async (v, { internalValueRef, customEventsKey }) => {
      setDescriptionOpen(true)
      const getParams = (v: string): UpdateBlockParams => ({
        id: block.id,
        data: {
          type: block.type,
          description: v,
        } satisfies Partial<Block>,
        instanceChildTarget: block.instanceChildTarget,
        strategy: 'merge',
        path,
        isoNow: new Date().toISOString(),
      })
      const result = await replicache?.mutate.updateBlock(getParams(v))
      if (result?.undo) {
        undoManager.current?.push({
          redo: [
            getInputFocusOp(`el-${sourceBlockId}-pp-description`),
            { op: 'updateBlock', args: getParams(internalValueRef.current) },
            {
              op: 'customWindowEvent',
              args: { name: customEventsKey, detail: { action: 'sync' } },
            },
          ],
          undo: [
            getInputFocusOp(`el-${sourceBlockId}-pp-description`),
            {
              op: 'blockBatchMutations',
              args: { mutations: result.undo },
            },
            {
              op: 'customWindowEvent',
              args: { name: customEventsKey, detail: { action: 'sync' } },
            },
          ],
        })
      }
    },
  })

  const { title: blockTitle } = React.useMemo(
    () =>
      getTitleBasedOnSelectedVariants({
        block,
        variantsToFilterBy: selectedDashboardVariants,
        parent: parent?.value,
      }),
    [block, parent?.value, selectedDashboardVariants]
  )

  let isApiNameReadOnly = true
  let isSlugReadOnly = true
  if (editorMode === 'write:all') {
    if (parent && !isInstanceBlock(parent.value)) {
      isApiNameReadOnly = false
      isSlugReadOnly = false
    }
  } else if (
    editorMode === 'write:value-only' ||
    editorMode === 'write:value-and-title'
  ) {
    if (
      parent &&
      (isListBlock(parent.value) || isReferenceBlock(parent.value))
    ) {
      isSlugReadOnly = false
    }
  }

  if (!blockTreeManager) return null
  return (
    <PropertiesContainer>
      <CollapsibleGroup
        id="api-name-&-slug"
        noBorderTop
        title="API Name and Slug"
        className="!pt-4"
        disabled={!parent || isAgentBlock(block)}
      >
        <AlwaysVisibleContent className="mb-2">
          <TextOption
            fallbackInViewMode="Untitled"
            name="Title"
            id={`el-${sourceBlockId}-pp-title`}
            value={blockTitle}
            forceEditable={forceEditable}
            mutate={async (v) => {
              const params: UpdateBlockParams = {
                id: block.id,
                data: getTitleUpdateOverride({
                  title: v,
                  block,
                  selectedVariants: selectedDashboardVariants,
                  parent: parent?.value,
                }),
                instanceChildTarget: block.instanceChildTarget,
                strategy: 'merge-deep',
                path,
                isoNow: new Date().toISOString(),
              }
              const result = await replicache?.mutate.updateBlock(params)
              if (result?.undo) {
                undoManager.current?.push({
                  redo: [
                    getInputFocusOp(`el-${sourceBlockId}-pp-title`),
                    { op: 'updateBlock', args: params },
                    {
                      op: 'customWindowEvent',
                      args: {
                        name: `${sourceBlockId}-pp-title`,
                        detail: { action: 'sync' },
                      },
                    },
                  ],
                  undo: [
                    getInputFocusOp(`el-${sourceBlockId}-pp-title`),
                    {
                      op: 'blockBatchMutations',
                      args: { mutations: result.undo },
                    },
                    {
                      op: 'customWindowEvent',
                      args: {
                        name: `${sourceBlockId}-pp-title`,
                        detail: { action: 'sync' },
                      },
                    },
                  ],
                })
              }
            }}
            titleAction={{
              label: 'Capitalize',
              onClick: ({ value, onChange }) => {
                onChange(title(value))
              },
            }}
          >
            {parent &&
              !isInstanceBlock(parent.value) &&
              !isInstanceBlock(block) &&
              !isDocumentBlock(block) &&
              !isComponentBlock(block) && (
                <IconButton
                  className="absolute right-1 top-1/2 -translate-y-1/2"
                  tooltip={{
                    content: isDescriptionOpen
                      ? 'Remove description'
                      : 'Add description',
                  }}
                  onClick={() => {
                    if (hasDescription) {
                      toast.error(
                        `You should remove the description to collapse it`
                      )
                    }
                    setDescriptionOpen((prev) => !prev)
                    setTimeout(() => {
                      syncedDescriptionProps.ref.current?.focus()
                    }, 100)
                  }}
                >
                  <InfoCircledIcon className="text-dark-muted" />
                </IconButton>
              )}
          </TextOption>
          {editorMode !== 'write:all' ? (
            <>
              {syncedDescriptionProps.value && (
                <span className="leading-[1.35] text-dark-dim !mt-0 text-xs">
                  {syncedDescriptionProps.value}
                </span>
              )}
            </>
          ) : (
            <Input
              asChild
              onBlur={sync}
              autoComplete="off"
              type="text"
              size="xs"
              placeholder="Add block description here..."
              disabled={
                (parent && isInstanceBlock(parent.value)) ||
                isInstanceBlock(block)
              }
              className={clsx(
                'py-2 min-h-18 hidden !mt-1.5',
                (isDescriptionOpen || hasDescription) && '!block'
              )}
            >
              <AutoGrowingTextarea
                onChange={(e) => onChange(e.target.value)}
                deps={[syncedDescriptionProps.value]}
                maxLength={320}
                style={{ maxHeight: 200, overflowY: 'auto' }}
                onFocus={() => {
                  setDescriptionOpen(true)
                }}
                id={`el-${sourceBlockId}-pp-description`}
                {...syncedDescriptionProps}
              />
            </Input>
          )}
        </AlwaysVisibleContent>

        <CollapsibleContent spacing="tight">
          {/**
        ApiName:
        - is not editable collection rows 🗄️
        - is not editable in rich text instances 📝
        */}
          {parent && (
            <SlugInput
              name="API Name"
              id={`el-${sourceBlockId}-pp-api-name`}
              errorMessage="API Name already exists"
              mutate={async (v) => {
                const params: UpdateApiNameParams = {
                  blockId: block.id,
                  apiName: v || null,
                  path,
                  isoNow: new Date().toISOString(),
                }
                const result = await replicache?.mutate.updateApiName(params)
                if (result?.undo) {
                  undoManager.current?.push({
                    redo: [
                      getInputFocusOp(`el-${sourceBlockId}-pp-api-name`),
                      { op: 'updateApiName', args: params },
                      {
                        op: 'customWindowEvent',
                        args: {
                          name: `${sourceBlockId}-pp-api-name`,
                          detail: { action: 'sync' },
                        },
                      },
                    ],
                    undo: [
                      getInputFocusOp(`el-${sourceBlockId}-pp-api-name`),
                      {
                        op: 'blockBatchMutations',
                        args: { mutations: result.undo },
                      },
                      {
                        op: 'customWindowEvent',
                        args: {
                          name: `${sourceBlockId}-pp-api-name`,
                          detail: { action: 'sync' },
                        },
                      },
                    ],
                  })
                }
              }}
              entity={{ id: block.id, value: block.apiName }}
              autoGenerated={autoGeneratedValues._apiName ?? null}
              checkIfAlreadyExists={(value) =>
                checkIfApiNameExists({
                  blockId: block.id,
                  blockTreeManager,
                  apiName: value,
                })
              }
              normalizeValue={getApiNameFromTitle}
              readOnly={isApiNameReadOnly}
            />
          )}
          {/*
          Slug:   
            - Goes in:
            - collection rows, documents, components, instances, nested components/instances
            - shouldn't be editable in nested instances, just read only there
        */}
          {parent &&
            (isDocumentBlock(block) ||
              isInstanceBlock(block) ||
              isComponentBlock(block)) && (
              <SlugInput
                name="Slug"
                id={`el-${sourceBlockId}-pp-slug`}
                entity={{ id: block.id, value: block.slug }}
                checkIfAlreadyExists={(value) =>
                  checkIfSlugExists({
                    blockId: block.id,
                    blockTreeManager,
                    slug: value,
                  })
                }
                normalizeValue={getSlugFromTitle}
                autoGenerated={autoGeneratedValues._slug ?? null}
                mutate={async (v) => {
                  const params: UpdateSlugParams = {
                    blockId: block.id,
                    slug: v || null,
                    path,
                    isoNow: new Date().toISOString(),
                  }
                  const result = await replicache?.mutate.updateSlug(params)
                  if (result?.undo) {
                    undoManager.current?.push({
                      redo: [
                        getInputFocusOp(`el-${sourceBlockId}-pp-api-name`),
                        { op: 'updateSlug', args: params },
                        {
                          op: 'customWindowEvent',
                          args: {
                            name: `${sourceBlockId}-pp-api-name`,
                            detail: { action: 'sync' },
                          },
                        },
                      ],
                      undo: [
                        getInputFocusOp(`el-${sourceBlockId}-pp-api-name`),
                        {
                          op: 'blockBatchMutations',
                          args: { mutations: result.undo },
                        },
                        {
                          op: 'customWindowEvent',
                          args: {
                            name: `${sourceBlockId}-pp-api-name`,
                            detail: { action: 'sync' },
                          },
                        },
                      ],
                    })
                  }
                }}
                readOnly={isSlugReadOnly}
              />
            )}
          {Boolean(children) && (
            <div className="mt-4 pt-4 border-t border-dark-border-solid">
              {children}
            </div>
          )}
        </CollapsibleContent>
      </CollapsibleGroup>
    </PropertiesContainer>
  )
}

export const SlugInput = ({
  name = 'Slug',
  id,
  errorMessage = 'Slug already exists',
  entity,
  autoGenerated,
  checkIfAlreadyExists,
  mutate,
  normalizeValue,
  readOnly,
}: {
  name?: string
  id: string
  errorMessage?: string
  entity: { id: string; value?: string | null }
  readOnly: boolean
  autoGenerated: string | null
  checkIfAlreadyExists: (value: string) => boolean
  mutate: (v: string | null) => Promise<void>
  normalizeValue: (value: string) => string
}) => {
  const [error, setError] = React.useState<string | null>(null)

  const {
    value: { value, isAutoGenerated },
    onChange,
    ref,
  } = useSyncedValue<
    HTMLInputElement,
    { value: string | null; isAutoGenerated: boolean }
  >({
    value: React.useMemo(
      () => ({
        value: entity.value ?? null,
        isAutoGenerated: !entity.value,
      }),
      [entity.value]
    ),
    setValue: ({ value }) => {
      if (!value) {
        mutate(null)
        setError(null)
        return
      }
      const normalized = normalizeValue(value)
      if (normalized === entity.value) {
        return setError(null)
      }
      if (normalized && checkIfAlreadyExists(normalized)) {
        return setError(errorMessage)
      }
      setError(null)
      mutate(normalized)
    },
    customEventsKey: id,
  })

  const onKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      onChange({
        value: e.currentTarget.value
          ? normalizeValue(e.currentTarget.value)
          : null,
        isAutoGenerated: !e.currentTarget.value,
      })
    }
  }

  const onBlur = async (e: React.FocusEvent<HTMLInputElement>) => {
    onChange({
      value: e.currentTarget.value
        ? normalizeValue(e.currentTarget.value)
        : null,
      isAutoGenerated: !e.currentTarget.value,
    })
  }

  const onToggle = () => {
    if (isAutoGenerated) {
      onChange({ value: autoGenerated ?? '', isAutoGenerated: false })
      return
    } else {
      onChange({ value: null, isAutoGenerated: true })
    }
  }

  return (
    <PrimitiveVariableInput
      ref={ref}
      id={id}
      isAutoGenerated={isAutoGenerated}
      value={value ?? ''}
      onChange={(e) => onChange((p) => ({ ...p, value: e.target.value }))}
      onBlur={onBlur}
      error={error}
      onToggle={onToggle}
      onKeyDown={onKeyDown}
      label={name}
      defaultValue={autoGenerated ?? ''}
      readOnly={readOnly}
    />
  )
}

const PrimitiveVariableInput = React.forwardRef<
  HTMLInputElement,
  {
    id: string
    isAutoGenerated: boolean
    value: string
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
    onBlur: (e: React.FocusEvent<HTMLInputElement>) => void
    error: string | null
    onToggle: () => void
    label: string
    defaultValue: string
    readOnly?: boolean
    onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void
  }
>(
  (
    {
      id,
      value,
      isAutoGenerated,
      onChange,
      onBlur,
      error,
      onToggle,
      label,
      defaultValue,
      readOnly,
      onKeyDown,
    },
    ref
  ) => {
    const internalRef = React.useRef<HTMLInputElement>(null)
    const shouldHideInput = readOnly || isAutoGenerated

    return (
      <Label
        htmlFor={id}
        size="xs"
        className={clsx(shouldHideInput && '[&>span]:!pb-0.5')}
        label={
          <span className="flex justify-between w-full items-baseline">
            <span className="items-center">{label}</span>
            {!readOnly && (
              <Button
                unstyled
                focusStyled
                className={clsx(
                  $labelButton(),
                  'text-xs font-normal cursor-default'
                )}
                onClick={() => {
                  onToggle()
                  setTimeout(() => {
                    internalRef.current?.focus()
                  }, 10)
                }}
                id={isAutoGenerated ? `${id}-trigger` : undefined}
              >
                {isAutoGenerated ? 'Override' : 'Auto-fill'}
              </Button>
            )}
          </span>
        }
        blocked={false}
        horizontal={false}
        onClick={(e) => {
          // fixes a weird bug where the button is clicked when an unrelated element under this label is clicked
          if (e.target instanceof HTMLElement) {
            if (e.target.tagName === 'BUTTON' || e.target.closest('button')) {
              return
            }
          }
          e.preventDefault()
          e.stopPropagation()
        }}
      >
        {shouldHideInput ? (
          <p className="leading-normal text-xs text-dark-bright overflow-hidden text-ellipsis whitespace-nowrap">
            {value || defaultValue}
          </p>
        ) : (
          <>
            <Input
              className={clsx(error && 'border-dark-error', 'w-full')}
              id={id}
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              autoComplete="off"
              type="text"
              size="xs"
              // eslint-disable-next-line react-compiler/react-compiler
              ref={mergeRefs([ref, internalRef])}
              onKeyDown={onKeyDown}
              placeholder="Empty"
            />
            {error && <p className="text-dark-error text-xs mt-1">{error}</p>}
          </>
        )}
      </Label>
    )
  }
)

export const Paragraph = ({
  children,
  className,
}: {
  children?: React.ReactNode
  className?: string
}) => {
  return (
    <p className={clsx(className, 'text-dark-faint text-xs')}>{children}</p>
  )
}

export const ConfigureContainer = ({
  children,
}: {
  children?: React.ReactNode
  block: Block | null
}) => {
  const editorMode = useEditorMode()
  if (editorMode !== 'write:all') return null

  return <PropertiesContainer>{children}</PropertiesContainer>
}

export const ViewContainer = ({ children }: { children?: React.ReactNode }) => {
  const editorMode = useEditorMode()

  if (editorMode === 'write:all') return null
  return <>{children}</>
}

export const PropertiesContainer = ({
  children,
}: {
  children?: React.ReactNode
}) => {
  const selectedTab__PREFERENCES = useUserPreferences(
    (s) => s.propertiesPanelMode
  )
  const [selectedTab__PUBLIC] = usePropertiesPanelMode__PUBLIC()
  const optimisticUserId = useUserStore((s) => s.optimisticUser?.id)
  const replicacheInstance = useRepoReplicache().data?.replicache.clientID

  const selectedTab = React.useMemo(() => {
    if (!optimisticUserId || !replicacheInstance) {
      return selectedTab__PUBLIC
    }
    return selectedTab__PREFERENCES
  }, [
    optimisticUserId,
    replicacheInstance,
    selectedTab__PREFERENCES,
    selectedTab__PUBLIC,
  ])

  if (selectedTab !== 'properties') return null
  return <>{children}</>
}

export const CommonSearchConfigGroup = ({
  block,
  parent,
  path,
  label = 'Index block',
  tooltipText = 'If checked, this block will be indexed into BaseHub Search. If unchecked, this block will not be indexed.',
}: {
  block: Block
  parent: Block | null
  path: string
  label?: string
  tooltipText?: string
}) => {
  const { data } = useRepoReplicache()
  const replicache = data?.replicache
  const editorMode = useEditorMode()
  const tree = useWorkingTreeStore((s) => s.tree)
  const { embeddings } = useFlags()
  const undoManager = useRepoUndoManager()
  const activeTabKey = useTabsStore((s) => s.activeTab?.key)
  const sourceBlockId = idToSourceBlockId(block)
  const getInputFocusOp = React.useCallback(
    (id: string) => {
      return getBlockFocusOp({
        block: {
          id: block.id,
          sourceBlockId: block.sourceBlockId,
        },
        preferredTabKey: activeTabKey,
        focusType: 'block:properties-panel',
        elementIdOverride: id,
      })
    },
    [block.id, block.sourceBlockId, activeTabKey]
  )

  const disabled =
    isInstanceBlock(block) || (parent ? isInstanceBlock(parent) : false)

  const value = useTreeSlice(tree, (s) => {
    let value: boolean | undefined
    if (isInstanceBlock(block)) {
      const masterBlock = s.getNode?.(block.typeOptions.targetBlockId, {
        fallbackKey: 'sourceBlockId',
        required: true,
      })
      value = masterBlock?.value?.searchConfig?.index
    } else if (parent && isInstanceBlock(parent)) {
      invariant(
        block.instanceChildTarget,
        'block.instanceChildTarget not found'
      )
      const targetNode = s.getNode?.(block.instanceChildTarget, {
        fallbackKey: 'sourceBlockId',
        required: true,
      })
      value = targetNode?.value?.searchConfig?.index
    } else {
      value = block.searchConfig?.index
    }
    return value
  })

  return (
    <Group title="Search" spacing="tight">
      <BooleanOption
        name={label}
        id={`el-${sourceBlockId}-pp-search-config-index`}
        value={!!value}
        mutate={async (v) => {
          const params: UpdateBlockParams = {
            id: block.id,
            data: {
              type: block.type,
              searchConfig: {
                index: v,
              },
            },
            instanceChildTarget: block.instanceChildTarget,
            path,
            strategy: 'merge-deep',
            isoNow: new Date().toISOString(),
          }
          const result = await replicache?.mutate.updateBlock(params)
          if (result?.undo) {
            undoManager.current?.push({
              redo: [
                getInputFocusOp(`el-${sourceBlockId}-pp-search-config-index`),
                { op: 'updateBlock', args: params },
              ],
              undo: [
                getInputFocusOp(`el-${sourceBlockId}-pp-search-config-index`),
                {
                  op: 'blockBatchMutations',
                  args: { mutations: result.undo },
                },
              ],
            })
          }
        }}
        disabled={editorMode !== 'write:all' || disabled}
        tooltipText={tooltipText}
      />
      {isComponentBlock(block) && embeddings && (
        <>
          <BooleanOption
            name="Generate embeddings (alpha)"
            value={!!block.searchConfig?.generateEmbeddings?.enabled}
            id={`el-${sourceBlockId}-pp-search-config-embeddings`}
            mutate={async (v) => {
              const params: UpdateBlockParams = {
                id: block.id,
                data: {
                  type: block.type,
                  searchConfig: {
                    generateEmbeddings: {
                      enabled: v,
                    },
                  },
                },
                instanceChildTarget: block.instanceChildTarget,
                path,
                strategy: 'merge-deep',
                isoNow: new Date().toISOString(),
              }
              const result = await replicache?.mutate.updateBlock(params)
              if (result?.undo) {
                undoManager.current?.push({
                  redo: [
                    getInputFocusOp(
                      `${sourceBlockId}-pp-search-config-embeddings`
                    ),
                    { op: 'updateBlock', args: params },
                  ],
                  undo: [
                    getInputFocusOp(
                      `${sourceBlockId}-pp-search-config-embeddings`
                    ),
                    {
                      op: 'blockBatchMutations',
                      args: { mutations: result.undo },
                    },
                  ],
                })
              }
            }}
            disabled={editorMode !== 'write:all' || disabled}
            tooltipText="Used for AI search"
          />
          {block.searchConfig?.generateEmbeddings?.enabled && (
            <TextOption
              multiLine
              name="System Prompt"
              id={`el-${sourceBlockId}-pp-search-config-embeddings-prompt`}
              value={
                block.searchConfig?.generateEmbeddings?.systemPrompt ??
                `You are an AI assistant created by BaseHub, specialized on question-answering. You can only make conversations based on the provided context. If a response cannot be formed strictly using the provided context, politely say you do not have knowledge about that topic. If the user asks about your name, you should introduce yourself as BaseHub Assistant. Be short and concise. Don't go off topic, or say anything that is not related to the conversation. Don't hallucinate.`
              }
              mutate={async (v) => {
                const params: UpdateBlockParams = {
                  id: block.id,
                  data: {
                    type: block.type,
                    searchConfig: {
                      generateEmbeddings: {
                        systemPrompt: v,
                      },
                    },
                  },
                  instanceChildTarget: block.instanceChildTarget,
                  path,
                  strategy: 'merge-deep',
                  isoNow: new Date().toISOString(),
                }
                const result = await replicache?.mutate.updateBlock(params)
                if (result?.undo) {
                  undoManager.current?.push({
                    redo: [
                      getInputFocusOp(
                        `${sourceBlockId}-pp-search-config-embeddings-prompt`
                      ),
                      { op: 'updateBlock', args: params },
                      {
                        op: 'customWindowEvent',
                        args: {
                          name: `${sourceBlockId}-pp-search-config-embeddings-prompt`,
                          detail: { action: 'sync' },
                        },
                      },
                    ],
                    undo: [
                      getInputFocusOp(
                        `${sourceBlockId}-pp-search-config-embeddings-prompt`
                      ),
                      {
                        op: 'blockBatchMutations',
                        args: { mutations: result.undo },
                      },
                      {
                        op: 'customWindowEvent',
                        args: {
                          name: `${sourceBlockId}-pp-search-config-embeddings-prompt`,
                          detail: { action: 'sync' },
                        },
                      },
                    ],
                  })
                }
              }}
            />
          )}
        </>
      )}
    </Group>
  )
}

export const TextOptionWithPresets = ({
  name,
  id,
  value,
  mutate,
  placeholder,
  type = 'text',
  tooltipText,
  forceEditable,
  inputProps,
  fallbackInViewMode,
  disabled = false,
  horizontal = false,
  className,
  titleAction,
  presets,
}: {
  name: string
  id: string
  value: string
  type?: string
  fallbackInViewMode?: string
  mutate: (newValue: string) => void
  placeholder?: string
  tooltipText?: string
  forceEditable?: boolean
  inputProps?: React.ComponentProps<typeof Input>
  horizontal?: boolean
  className?: string
  disabled?: boolean
  titleAction?: {
    label: React.ReactNode
    onClick: (opts: { value: string; onChange: (v: string) => void }) => void
  }
  presets: Array<{ title: string; value: string }>
}) => {
  const {
    onChange,
    sync,
    valueRef: _,
    ...syncedInputProps
  } = useSyncedValue<HTMLInputElement>({
    customEventsKey: id,
    value,
    setValue: mutate,
  })
  const editorMode = useEditorMode()
  const viewMode = editorMode !== 'write:all' && !forceEditable

  if (viewMode && !syncedInputProps.value && !fallbackInViewMode) return null
  return (
    <Label
      htmlFor={id}
      label={
        <span
          className={clsx(
            'flex justify-between relative w-full items-baseline',
            !viewMode && 'min-w-[5.65rem]'
          )}
        >
          <span className="items-center flex">
            {name}
            {tooltipText && (
              <Tooltip delayDuration={500}>
                <TooltipTrigger asChild>
                  <InfoCircledIcon className="ml-1 text-dark-muted hover:text-dark-bright" />
                </TooltipTrigger>
                <TooltipContent>{tooltipText}</TooltipContent>
              </Tooltip>
            )}
          </span>
          {!disabled && titleAction && !viewMode && (
            <Button
              unstyled
              focusStyled
              className={clsx(
                $labelButton(),
                'text-xs font-normal cursor-default'
              )}
              onClick={() => {
                titleAction.onClick({ value: syncedInputProps.value, onChange })
                const capitalized = title(syncedInputProps.value)
                onChange(capitalized)
              }}
            >
              {titleAction.label}
            </Button>
          )}
        </span>
      }
      size="xs"
      blocked={false}
      className={clsx(
        className,
        !horizontal && '[&>span]:!pb-0.5',
        'relative',
        viewMode && '[&>span]:!mr-0'
      )}
      horizontal={horizontal}
    >
      <DropdownMenu>
        <div className="relative">
          {viewMode ? (
            <span className="text-dark-bright">
              {presets.find((preset) => preset.value === syncedInputProps.value)
                ?.title ?? syncedInputProps.value}
            </span>
          ) : (
            <>
              <Input
                id={id}
                name={name}
                onBlur={sync}
                onChange={(e) => onChange(e.target.value)}
                autoComplete="off"
                type={type}
                size="xs"
                placeholder={placeholder}
                disabled={disabled}
                {...inputProps}
                {...{
                  ...syncedInputProps,
                  value:
                    presets.find(
                      (preset) => preset.value === syncedInputProps.value
                    )?.title ?? syncedInputProps.value,
                }}
                className={clsx(
                  'pr-5 !max-w-[182px]',
                  clsx(disabled && 'cursor-not-allowed', inputProps?.className)
                )}
              />
              <DropdownMenuTrigger
                className="absolute right-0.5 top-1/2 -translate-y-1/2 p-0.5"
                asChild
                disabled={disabled}
              >
                <IconButton>
                  <CaretSortIcon />
                </IconButton>
              </DropdownMenuTrigger>

              <DropdownMenuContent side="bottom" sideOffset={8} align="end">
                {presets.map((preset) => (
                  <DropdownMenuItem
                    key={preset.title}
                    onClick={() => {
                      onChange(preset.value)
                      sync()
                    }}
                  >
                    {preset.title}
                    <span className="text-dark-muted ml-1 text-xs max-w-[20ch] truncate">
                      {preset.value}
                    </span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </>
          )}
        </div>
      </DropdownMenu>
    </Label>
  )
}

export const ColorPickerInput = ({
  value,
  mutate,
  options,
  name,
  id,
}: {
  value: string
  options: string[] | { id: string; value: string; tooltip?: string }[]
  mutate: (newValue: string) => void
  name: string
  id: string
}) => {
  return (
    <div className="flex gap-y-1 flex-col pb-2">
      <span className="text-dark-muted text-xs font-medium leading-normal">
        {name}
      </span>
      <div
        className="flex gap-1 focus-visible:outline-none flex-wrap"
        id={id}
        tabIndex={0}
      >
        {options.map((_color, idx) => {
          const tooltip =
            typeof _color === 'string' ? _color : _color.tooltip || _color.value
          const color = typeof _color === 'string' ? _color : _color.value

          return (
            <SimpleTooltip content={tooltip} key={idx}>
              <button
                onClick={() => mutate(color)}
                className={clsx(
                  'cursor-default rounded-full size-[1.125rem] flex items-center justify-center outline-transparent outline-offset-0 outline outline-1 focus-visible:outline-dark-control-base',
                  color === value &&
                    '!outline-dark-bright border-dark-bg border-2'
                )}
                style={{ backgroundColor: getSolidColor(color) }}
              >
                {color === value && (
                  <CheckIcon className="size-3 text-dark-bg" />
                )}
              </button>
            </SimpleTooltip>
          )
        })}
      </div>
    </div>
  )
}

export const LimitInput = ({
  name,
  minId,
  maxId,
  value,
  mutateMin,
  mutateMax,
  placeholder = '-',
  tooltipText,
  forceEditable,
  disabled = false,
  className,
  max = 999_999,
}: {
  name: string
  minId: string
  maxId: string
  value: { min: number | null; max: number | null }
  mutateMin: (value: number | null) => void
  mutateMax: (value: number | null) => void
  placeholder?: string
  tooltipText?: string
  forceEditable?: boolean
  disabled?: boolean
  className?: string
  max?: number
}) => {
  const editorMode = useEditorMode()
  const viewMode = editorMode !== 'write:all' && !forceEditable

  const {
    onChange: onMinChange,
    sync: syncMin,
    ...syncedMinProps
  } = useSyncedValue<HTMLInputElement>({
    value: value.min?.toString() ?? '',
    customEventsKey: minId,
    setValue: (v) => {
      const num = v ? Math.min(parseInt(v, 10), max) : null

      if (num === null || isNaN(num)) {
        mutateMin(null)
        return
      }
      if (value.max !== null && num > value.max) {
        mutateMin(value.max)
        return
      }
      mutateMin(num)
    },
  })

  const {
    onChange: onMaxChange,
    sync: syncMax,
    ...syncedMaxProps
  } = useSyncedValue<HTMLInputElement>({
    value: value.max?.toString() ?? '',
    customEventsKey: maxId,
    setValue: (v) => {
      const num = v ? Math.min(parseInt(v, 10), max) : null

      if (num === null || isNaN(num)) {
        mutateMax(null)
        return
      }
      if (value.min !== null && num < value.min) {
        mutateMax(value.min)
        return
      }
      mutateMax(num)
    },
  })

  const hasValue = value.min !== null || value.max !== null
  if (viewMode && !hasValue) return null

  return (
    <div
      className={clsx('flex gap-1.5 justify-between items-center', className)}
    >
      <Label
        horizontal
        label={
          <span className="inline-flex items-center">
            {name}
            {tooltipText && (
              <Tooltip delayDuration={500}>
                <TooltipTrigger asChild>
                  <InfoCircledIcon className="ml-1 text-dark-muted hover:text-dark-bright" />
                </TooltipTrigger>
                <TooltipContent>{tooltipText}</TooltipContent>
              </Tooltip>
            )}
          </span>
        }
        size="xs"
        blocked={false}
      />
      <div className="flex items-center w-fit max-w-fit flex-none space-x-2">
        {viewMode ? (
          <span className="text-dark-bright text-xs mr-2">
            Min {syncedMinProps.value || 'Unset'}
          </span>
        ) : (
          <LimitNumberInput
            id={minId}
            name={`${name}-min`}
            value={syncedMinProps.value}
            ref={syncedMinProps.ref}
            onBlur={syncMin}
            onChange={(e) => onMinChange(e.target.value)}
            autoComplete="off"
            type="number"
            label="Min"
            placeholder={placeholder}
            className={clsx(disabled && 'cursor-not-allowed')}
            disabled={disabled}
            min={0}
            max={max}
          />
        )}
        {viewMode ? (
          <span className="text-dark-bright text-xs">
            Max {syncedMaxProps.value || 'Unset'}
          </span>
        ) : (
          <LimitNumberInput
            id={maxId}
            label="Max"
            name={`${name}-max`}
            value={syncedMaxProps.value}
            ref={syncedMaxProps.ref}
            onBlur={syncMax}
            onChange={(e) => onMaxChange(e.target.value)}
            autoComplete="off"
            type="number"
            placeholder={placeholder}
            className={clsx(disabled && 'cursor-not-allowed')}
            disabled={disabled}
            min={value.min ?? 0}
            max={max}
          />
        )}
      </div>
    </div>
  )
}

type LimitInputNumberProps = {
  name: string
  value?: string | null
  label: string
  max?: number
} & React.InputHTMLAttributes<HTMLInputElement>

const LimitNumberInput = React.forwardRef<
  HTMLInputElement,
  LimitInputNumberProps
>(({ label, className, max, ...props }, ref) => {
  const inputLength = props.value?.length ?? 0
  return (
    <div
      className={clsx(
        'border relative border-dark-border-solid bg-dark-shade rounded flex items-stretch w-fit group'
      )}
    >
      <label
        className="border-r text-xs text-dark-muted border-dark-border-solid flex items-center px-1.5 whitespace-nowrap font-medium"
        htmlFor={props.id}
      >
        {label}
      </label>
      <input
        className={clsx(
          'border-none px-1.5 text-xs text-dark-bright py-0.5',
          'focus-visible:outline-none focus-visible:ring-0',
          'text-center text-xs bg-transparent',
          '[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none',
          'placeholder:text-dark-faint w-[--input-width] min-w-7',
          className
        )}
        style={
          {
            '--input-width': `${inputLength + 3 * 0.75}ch`,
            maxWidth: '7ch',
          } as React.CSSProperties
        }
        max={max}
        onChange={(e) => {
          if (max && props.value && Number(props.value) > max) {
            e.preventDefault()
            e.stopPropagation()
          }
        }}
        {...props}
        ref={ref}
      />
    </div>
  )
})

export const Accordion = ({ children }: { children: React.ReactNode }) => {
  return (
    <RadixAccordion.Root type="multiple" className="flex flex-col gap-2">
      <div className="flex flex-col">{children}</div>
    </RadixAccordion.Root>
  )
}

export const AccordionItem = ({
  children,
  className,
  value,
}: {
  children: React.ReactNode
  className?: string
  value: string
}) => {
  return (
    <RadixAccordion.Item value={value} className={className}>
      {children}
    </RadixAccordion.Item>
  )
}

export const AccordionContent = ({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) => {
  return (
    <RadixAccordion.Content
      className={clsx(
        className,
        'radix-state-open:animate-slide-down radix-state-closed:animate-slide-up overflow-hidden'
      )}
    >
      {children}
    </RadixAccordion.Content>
  )
}

export const AccordionTrigger = ({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) => {
  return (
    <RadixAccordion.Trigger
      className={clsx(
        'cursor-default focus-visible:outline focus-visible:outline-dark-control-base rounded',
        className
      )}
    >
      {children}
    </RadixAccordion.Trigger>
  )
}

export type SetOption = {
  label: string
  key: string
  href: string
  parentKey: string
  icon: React.ReactNode
}

export type CheckListOption =
  | {
      label: string
      value: string
      href: string
      parentKey: string
      icon: React.ReactNode
      description?: string
    }
  | {
      label: string
      value: string
      href?: never
      parentKey?: never
      icon: React.ReactNode
      description?: string
    }

/** Works for affected blocks by variant sets and for media formats */
export function MultiCheckListsOption<T extends CheckListOption>({
  title,
  availableOptionSets,
  iconButtonProps,
  setsInUse,
  getItemsByCheckList,
  getCheckedChildren,
  handleItemsToggle,
  setOption,
  checkListPopoverProps,
  emptyState,
  removeLabel = 'Remove variant set',
  className,
}: {
  title: string
  className?: string
  iconButtonProps?: IconButtonProps
  availableOptionSets: T[]
  setsInUse: T[]
  getItemsByCheckList: (checkListValue: T['value']) => CheckItem[]
  getCheckedChildren: (setId: string) => CheckItem[]
  setOption: (optionValue: string, enabled: boolean) => void
  handleItemsToggle: (args: {
    newValue: boolean | 'overwrite'
    items: CheckItem[]
    setId: string
  }) => void
  emptyState?: string
  removeLabel?: string
  checkListPopoverProps?: Partial<
    Pick<
      CheckListPopoverProps,
      | 'title'
      | 'allowedLabel'
      | 'notAllowedLabel'
      | 'prefix'
      | 'itemName'
      | 'filterById'
    >
  >
}) {
  const addTab = useTabsStore((s) => s.addTab)
  const editorMode = useEditorMode()
  const readOnly = editorMode !== 'write:all'
  return (
    <div
      className={clsx(
        'border-dark-border-solid flex flex-col border-t py-4 -mr-1',
        className
      )}
    >
      <div className="flex justify-between w-full leading-normal items-center h-[1.3125rem] relative text-dark-bright">
        <span className="font-medium">{title}</span>
        {editorMode === 'write:all' &&
          availableOptionSets.length > 0 &&
          (availableOptionSets.length === 1 ? (
            <IconButton
              {...iconButtonProps}
              className={clsx(iconButtonProps?.className, 'size-[22px]')}
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                setOption(availableOptionSets[0]!.value, true)
                return
              }}
            >
              <PlusIcon className="size-3.5" />
            </IconButton>
          ) : (
            <DropdownMenu>
              <DropdownMenuTrigger
                asChild
                disabled={!availableOptionSets.length}
              >
                <IconButton
                  {...iconButtonProps}
                  className={clsx(iconButtonProps?.className, 'size-[22px]')}
                >
                  <PlusIcon className="size-3.5" />
                </IconButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                side="bottom"
                onCloseAutoFocus={(e) => e.preventDefault()}
                data-inner-popover
                className="shadow-modal"
              >
                {availableOptionSets.map((option) => (
                  <DropdownMenuItem
                    key={option.label}
                    className={clsx(
                      'flex gap-2 items-center px-2 pr-3 w-full text-sm',
                      'text-dark-dim'
                    )}
                    onClick={() => setOption(option.value, true)}
                  >
                    {option.icon}
                    <span>{option.label}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          ))}
      </div>

      {!setsInUse.length && emptyState && (
        <span className="text-xs text-dark-faint mt-1">{emptyState}</span>
      )}
      <div className="flex flex-col space-y-2 mt-2">
        {setsInUse.map((option) => {
          return (
            <CheckListPopover
              itemName="formats"
              title="formats"
              {...checkListPopoverProps}
              key={option.value}
              checkedOptions={getCheckedChildren(option.value)}
              options={getItemsByCheckList(option.value)}
              emptyState={
                !readOnly
                  ? (search) => {
                      const parsed = search
                        .trim()
                        .replace(/\s.+$/g, '')
                        .toLowerCase()
                      return (
                        <PopoverClose asChild>
                          <Button
                            unstyled
                            focusStyled
                            className="px-4 pb-2 text-xs font-medium text-dark-muted leading-normal hover:underline cursor-default"
                            onClick={() =>
                              handleItemsToggle({
                                newValue: true,
                                items: [{ name: parsed, id: parsed }],
                                setId: option.value,
                              })
                            }
                          >
                            Add "{parsed}" format.
                          </Button>
                        </PopoverClose>
                      )
                    }
                  : undefined
              }
              mutate={(newValue, options) =>
                handleItemsToggle({
                  newValue,
                  items: options,
                  setId: option.value,
                })
              }
              footer={
                !readOnly && (
                  <>
                    <hr className="mx-4 mb-3 border-dark-border-solid relative" />
                    <Button
                      className="mx-4 w-[-webkit-fill-available] text-dark-error mb-3"
                      intent="secondary"
                      icon={<TrashIcon />}
                      onClick={() => setOption(option.value, false)}
                    >
                      {removeLabel}
                    </Button>
                  </>
                )
              }
            >
              <div className="flex flex-wrap items-center gap-x-1.5">
                {option.icon}
                {option.href ? (
                  <ButtonLink
                    unstyled
                    focusStyled
                    href={option.href}
                    onClick={(e) => {
                      if (!e.metaKey) {
                        e.preventDefault()
                        e.stopPropagation()
                        addTab({
                          key: `${option.parentKey}#${option.value}`,
                          partiallyOpen: true,
                        })
                      }
                    }}
                    className="text-xs font-medium leading-normal text-dark-dim grow cursor-default hover:underline"
                  >
                    {option.label}
                  </ButtonLink>
                ) : (
                  <span className="text-xs font-medium leading-normal text-dark-dim grow cursor-default">
                    {option.label}
                  </span>
                )}
                <PopoverTrigger asChild>
                  <Button
                    unstyled
                    focusStyled
                    className="cursor-default p-0.5 px-1 hover:text-dark-bright text-dark-faint transition-colors"
                  >
                    <DotsHorizontalIcon className="size-3.5" />
                  </Button>
                </PopoverTrigger>
                {option.description && (
                  <span className="w-full text-dark-faint text-xs leading-normal text-pretty">
                    {option.description}
                  </span>
                )}
              </div>
            </CheckListPopover>
          )
        })}
      </div>
    </div>
  )
}
